/**
 * BlogPostPageClient.tsx
 *
 * Full Prototype G "Card Stack with Drawer" blog post layout.
 * Ported pixel-perfect from:
 *   /Users/lars/Documents/00 PKA/My Inbox/blog-sidebar-variants/
 *   prototype-sidebar-g-card-stack-copy-link.html
 *
 * Layout overview:
 *   - .blog-post-layout (scoping wrapper)
 *   - <BlogHero>         — dark hero: image + text col + subtitle band
 *   - <BreadcrumbStrip>  — 4-level breadcrumb on cream strip
 *   - <PrevNextStrip>    — cream prev/next navigation
 *   - .article-layout    — cream two-column: article body + desktop sidebar
 *   - <DrawerTab>        — fixed right-edge tab (< 1024px, after hero)
 *   - <DrawerBackdrop>   — click-to-close overlay
 *   - <DrawerPanel>      — slide-in panel with same 4 cards as sidebar
 *
 * Subcomponents (all defined in this file):
 *   BlogHero, BreadcrumbStrip, PrevNextStrip, ArticleBody,
 *   CopyPostLinkButton, TableOfContentsCard, ContinueReadingCard,
 *   SidebarNewsletterCard, ProMemberCard,
 *   BlogSidebar, DrawerTab, DrawerBackdrop, DrawerPanel
 *
 * Tina click-to-edit surfaces (10 total):
 *   Per-post:  title, subtitle, author, publishDate, body
 *   Global:    newsletter.heading, newsletter.microcopy,
 *              promember.heading, promember.body, promember.cta
 *
 * CSS: all component CSS lives in app/globals.css under the
 * .blog-post-layout scope. No inline style objects except where
 * a value is computed at runtime (e.g. transform for drawer).
 */

'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useId,
  memo,
} from 'react';
import { createPortal } from 'react-dom';

/*
 * useLayoutEffect warns when React renders this client component on the server
 * ("useLayoutEffect does nothing on the server"). The layout-effect timing is only
 * ever needed in the browser, so bind to useEffect during SSR. The choice is a
 * module-level constant, so hook order is stable — this is not a conditional hook.
 */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;
import Link from 'next/link';
import YouTubeEmbed from '@/components/blog/YouTubeEmbed';
import { VideoCoordinatorProvider } from '@/components/blog/VideoCoordinator';
import ReadNextSlider from '@/components/blog/ReadNextSlider';
import ProMemberBlock from '@/app/_client/ProMemberBlock';
import NewsletterForm from '@/components/NewsletterForm';
// New components — video-blog migrations cycle 5 (2026-05-09)
import WatchVideoLink from '@/components/blog/WatchVideoLink';
import PhilosophyCallout from '@/components/blog/PhilosophyCallout';
import StepTimeline, { StepTimelineItem } from '@/components/blog/StepTimeline';
import NumberedTimeline, { NumberedTimelineItem } from '@/components/blog/NumberedTimeline';
import VideoTutorialSectionV2, {
  VideoTutorialSectionV2Item,
  videoTutorialSectionV2ItemComponents,
} from '@/components/blog/VideoTutorialSectionV2';
import PhilosophyTag from '@/components/blog/PhilosophyTag';
import ReadingProgressBar from '@/components/blog/ReadingProgressBar';
import { Lightbox, type LightboxPhoto } from '@/components/photos/Lightbox';
// FloatingMiniPlayer removed — PIP-RESTART-FIX-01 (2026-05-06): floating logic
// moved into YouTubeEmbed.tsx (single-iframe pattern). No second iframe instance.
import { useMailcheck } from '@/hooks/useMailcheck';
import { useTurnstile } from '@/hooks/useTurnstile';
import { useNewsletterSubmit } from '@/hooks/useNewsletterSubmit';
import { usePhoneKeyboardBox } from '@/hooks/use-phone-keyboard-box';
import OrbitSpinner from '@/components/OrbitSpinner';
import { noOrphan } from '@/lib/no-orphan';
import type {
  PrevNextPosts,
  RecentBlogPost,
  SliderPost,
  BlogSidebarVariables,
} from '@/lib/content-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const categoryLabels: Record<string, string> = {
  technique: 'Technique',
  musicality: 'Musicality',
  practice: 'Practice',
  repertoire: 'Repertoire',
  mindset: 'Mindset',
  tips: 'Tips & Tricks',
  theory: 'Music Theory',
  'inspiration-music': 'Inspiration & Music',
  'piano-tutorials': 'Piano Tutorials',
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/**
 * Extract plain text from a React ReactNode.
 *
 * TinaMarkdown passes heading children as ReactNode (string, array, or element),
 * not as a plain string. `typeof children === 'string'` is false for compound
 * nodes, which caused every heading to render with id="" (empty slugify of '').
 *
 * This helper recurses into arrays and React elements to collect the leaf text
 * so slugify() always receives the full heading string.
 *
 * SSR fix (2026-05-06): TinaMarkdown v3 passes heading children as a nested
 * <TinaMarkdown content={astChildren} /> element, NOT as resolved React strings.
 * During SSR (and before hydration), React.ReactElement.props.children is
 * undefined for TinaMarkdown — the text lives in props.content, which is the
 * raw AST children array [{type:'text', text:'...'}, ...]. This branch extracts
 * leaf text directly from the AST so h2/h3 id= attributes are populated in the
 * SSR HTML and visible to Google and LLM crawlers.
 */
type TinaAstNode = { type: string; text?: string; children?: TinaAstNode[] };

function extractTextFromTinaAst(nodes: TinaAstNode[]): string {
  return nodes.map(n => {
    if (n.text) return n.text;
    if (n.children) return extractTextFromTinaAst(n.children);
    return '';
  }).join('');
}

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node !== null && typeof node === 'object' && 'props' in (node as object)) {
    const el = node as React.ReactElement<{ children?: React.ReactNode; content?: TinaAstNode[] }>;
    // TinaMarkdown SSR path: children wraps a <TinaMarkdown content={astArray} />
    // where props.children is undefined and text lives in props.content.
    if (el.props.content && Array.isArray(el.props.content)) {
      return extractTextFromTinaAst(el.props.content);
    }
    return extractText(el.props.children);
  }
  return '';
}

// Fixed 2026-07-09 (hydration mismatch #418): `timeZone: 'UTC'` is required.
// Without it, toLocaleDateString() uses the RUNTIME's local timezone — which
// is UTC on the Cloudflare Workers SSR server but whatever timezone the
// visitor's browser is in on the client. For any publishDate stored as a
// bare YYYY-MM-DD (parsed as midnight UTC), a visitor in a negative-UTC-offset
// timezone (e.g. any US timezone) would see the CALENDAR DATE roll back one
// day on the client relative to what the server rendered, and — worse for
// hydration — creates a text mismatch React has to repair on every load.
// Pinning UTC makes the displayed date depend only on the stored value, not
// on the runtime's or visitor's timezone, so server and client always agree.
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

// Fixed 2026-07-09 (hydration mismatch #418, root cause): frontmatter YAML
// dates (e.g. `publishDate: 2023-09-25`) are auto-parsed into native JS Date
// objects by the frontmatter loader (a well-known gray-matter/js-yaml
// behavior), not left as strings. Passing that Date object DIRECTLY into an
// HTML attribute (e.g. `dateTime={post.publishDate}`) makes React implicitly
// call the object's `.toString()` to serialize it — and `Date.prototype.toString()`
// is timezone-dependent (embeds the runtime's local offset and zone name).
// Confirmed via SSR-vs-post-hydration DOM diff on staging: the server (UTC)
// rendered `dateTime="Mon Sep 25 2023 09:08:36 GMT+0000 (Coordinated Universal Time)"`
// while the client browser (Europe/Amsterdam, UTC+2) rendered
// `dateTime="Mon Sep 25 2023 11:08:36 GMT+0200 (Central European Summer Time)"`
// for the SAME underlying instant — a real, byte-different attribute value,
// which is exactly the kind of "text" hydration mismatch React error #418
// reports. Fix: always convert to `.toISOString()` before use — that format
// is fixed-UTC ("Z" suffix) and therefore identical on every runtime/timezone
// combination, server or client.
function toIsoDateTimeString(dateLike?: string | null): string | undefined {
  if (!dateLike) return undefined;
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// ToC auto-generation from rendered headings
// ---------------------------------------------------------------------------
interface TocItem {
  id: string;
  text: string;
  level: 2 | 3;
}

function buildTocFromDOM(articleEl: HTMLElement): TocItem[] {
  const headings = articleEl.querySelectorAll('h2, h3');
  const items: TocItem[] = [];
  headings.forEach(h => {
    const id = h.getAttribute('id');
    const text = h.textContent ?? '';
    if (id && text) {
      items.push({ id, text, level: h.tagName === 'H3' ? 3 : 2 });
    }
  });
  return items;
}

// ---------------------------------------------------------------------------
// External link classifier
// External = href does NOT start with /, #, https://pianofantasy.com,
// https://www.pianofantasy.com, mailto:, or tel:.
// All other hrefs (in-page anchors, root-relative routes, own-domain URLs,
// mailto:, tel:) are treated as internal.
// Scope: blog post body only — does not affect any other link in the app.
// ---------------------------------------------------------------------------
const OWN_ORIGINS = [
  'https://pianofantasy.com',
  'https://www.pianofantasy.com',
];

function isExternalHref(href: string): boolean {
  if (!href) return false;            // empty or undefined — render as-is, no target
  if (href.startsWith('/')) return false;
  if (href.startsWith('#')) return false;
  if (href.startsWith('mailto:')) return false;
  if (href.startsWith('tel:')) return false;
  for (const origin of OWN_ORIGINS) {
    if (href.startsWith(origin)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// VideoTutorialSection — v3 (Aria brief 2026-05-06, Lars approved direction)
//
// REPLACES v2 card layout. Lars rejected v2 on 2026-05-06:
//   "Stop with the cards. Make it as a timeline that doesn't go straight."
//   "Use bigger icons. Don't put the icons in a container."
//
// v3 changes vs v2:
//   - All cards removed — text sits directly on cream blog body (#F4F0EA)
//   - Icons: uncontained, 64px desktop / 48px mobile, bare on cream
//   - Connector: organic S-curve SVG path in --gold (#D4B86E), NOT border-left
//   - Variant B selected by Lars (2026-05-06): single full-width column, no sidebar
//
// Icons: inline SVG, verbatim from Brand/BrandAssets/icons/philosophy-set-v1/.
//   All three icons use explicit stroke="#2A2233" (dark navy) and
//   stroke="#D4B86E" / fill="#D4B86E" (gold) — two-tone, NOT currentColor.
//   No <Icon> component exists in codebase; inline SVG is correct per the
//   architectural rule (secondary valid pattern for page-scoped shapes).
//
// CSS: globals.css under .vts3- scope (v3 namespace, distinct from v2 .video-card).
// ---------------------------------------------------------------------------

// ── Shared: icon SVG definitions (inline, verbatim from brand kit) ────────

/**
 * icon-philosophy-wrist-freedom.svg
 * Top-down open hand. Navy (#2A2233) strokes for hand anatomy.
 * Gold (#D4B86E) lateral arc at wrist — double-headed arrow.
 */
function IconWristFreedom({ size }: { size: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"
      strokeLinecap="round" strokeLinejoin="round"
      width={size} height={size} aria-hidden="true" focusable={false}>
      {/* Palm mass */}
      <path d="M22 42 C21 38 21 33 22 30 C23 27 26 26 32 26 C38 26 41 27 42 30 C43 33 43 38 42 42 C41 45 37 48 32 48 C27 48 23 45 22 42 Z"
        stroke="#2A2233" strokeWidth="1.7"/>
      {/* Wrist constriction */}
      <path d="M26 48 C26 50 27 52 28 53" stroke="#2A2233" strokeWidth="1.7"/>
      <path d="M38 48 C38 50 37 52 36 53" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Pinky */}
      <path d="M22 33 C19 31 17 28 17 25 C17 23 18.5 22.5 19.5 24 C20 25 20 28 21 30" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Ring finger */}
      <path d="M24 29 C23 26 22.5 22 23 19.5 C23.5 17.5 25 17 26 18.5 C26.5 20 26 24 26 27" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Middle finger */}
      <path d="M29 27 C29 23.5 28.5 19.5 29 17 C29.5 15 31 14.5 32 16 C32.5 17.5 32 22 32 26" stroke="#2A2233" strokeWidth="1.6"/>
      {/* Index finger */}
      <path d="M35 28 C35 24.5 35.5 20.5 36 18 C36.5 16 38 15.5 39 17 C39.5 18.5 39 23 38 27" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Thumb */}
      <path d="M42 34 C44 31.5 46 29 47 27 C48 25 47 23 45.5 23.5 C44.5 24 44 26 43 28" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Gold lateral arc at wrist */}
      <path d="M22 52 C25 55 32 56 38 55 C42 54 44 52 44 52" stroke="#D4B86E" strokeWidth="1.5"/>
      {/* Left arrowhead */}
      <path d="M22 52 L20 49 M22 52 L20 54" stroke="#D4B86E" strokeWidth="1.3"/>
      {/* Right arrowhead */}
      <path d="M44 52 L46 49 M44 52 L46 54" stroke="#D4B86E" strokeWidth="1.3"/>
    </svg>
  );
}

/**
 * icon-philosophy-performer-interpretation-v3.svg
 * Theater mask (face-on) with gold note. Navy mask, gold note + staff.
 */
function IconPerformerInterpretation({ size }: { size: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"
      strokeLinecap="round" strokeLinejoin="round"
      width={size} height={size} aria-hidden="true" focusable={false}>
      {/* Mask outer shape */}
      <path d="M10 22 C9 26 9 32 11 37 C13 42 17 45 21 44.5 C25 44 28 40 29 35 C30 30 29 24 27 19 C25 15 21 13.5 17 14.5 C13 15.5 11 18 10 22 Z"
        stroke="#2A2233" strokeWidth="1.8"/>
      {/* Left eye — gentle arc */}
      <path d="M13 25 C14.5 23 17 23 18 25" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Right eye — steep arc */}
      <path d="M20 23 C21 20.5 24 20.5 25 23.5" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Mouth */}
      <path d="M14 36 C16.5 38.5 21.5 38.5 24 36" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Center dividing line */}
      <path d="M19 14.5 C19 22 19 32 19 44.5" stroke="#2A2233" strokeWidth="0.9"/>
      {/* Gold notehead */}
      <path d="M41 40 C41 38.1 42.6 36.5 44.5 36.5 C46.4 36.5 48 38.1 48 40 C48 41.9 46.4 43.5 44.5 43.5 C42.6 43.5 41 41.9 41 40 Z"
        fill="#D4B86E" stroke="none"/>
      {/* Gold stem */}
      <path d="M47.8 38.5 L47.8 18" stroke="#D4B86E" strokeWidth="1.4"/>
      {/* Gold staff lines */}
      <path d="M36 44 C40 43.8 52 43.8 56 44" stroke="#D4B86E" strokeWidth="1.0"/>
      <path d="M36 40 C40 39.8 52 39.8 56 40" stroke="#D4B86E" strokeWidth="1.0"/>
      <path d="M36 36 C40 35.8 52 35.8 56 36" stroke="#D4B86E" strokeWidth="1.0"/>
    </svg>
  );
}

/**
 * icon-philosophy-multi-tempo.svg
 * Three eighth notes — uneven spacing implies different tempos.
 * Left and right noteheads navy (#2A2233); center notehead gold (#D4B86E).
 */
function IconMultiTempo({ size }: { size: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"
      strokeLinecap="round" strokeLinejoin="round"
      width={size} height={size} aria-hidden="true" focusable={false}>
      {/* Left dark eighth note */}
      <path d="M13 46 C12 43.5 14.5 41.5 17 42 C19.5 42.5 20.5 45 19.5 47 C18.5 49 16 49.5 14 48.5 C12.8 47.8 13 46.5 13 46"
        stroke="#2A2233" strokeWidth="1.6" fill="#2A2233"/>
      <path d="M19.5 45 L19.5 27" stroke="#2A2233" strokeWidth="1.6"/>
      <path d="M19.5 27 C24 29 25 33 22 36" stroke="#2A2233" strokeWidth="1.5" fill="none"/>
      {/* Center gold eighth note */}
      <ellipse cx="33" cy="44" rx="4" ry="3" fill="#D4B86E" stroke="none" transform="rotate(-15 33 44)"/>
      <path d="M36 42 L36 21" stroke="#D4B86E" strokeWidth="1.6"/>
      <path d="M36 21 C41 23.5 42.5 28 38.5 32" stroke="#D4B86E" strokeWidth="1.5" fill="none"/>
      {/* Right dark eighth note */}
      <path d="M46 46 C45 43.5 47.5 41.5 50 42 C52.5 42.5 53.5 45 52.5 47 C51.5 49 49 49.5 47 48.5 C45.8 47.8 46 46.5 46 46"
        stroke="#2A2233" strokeWidth="1.6" fill="#2A2233"/>
      <path d="M52.5 45 L52.5 24" stroke="#2A2233" strokeWidth="1.6"/>
      <path d="M52.5 24 C57 26.5 58 31 55 34" stroke="#2A2233" strokeWidth="1.5" fill="none"/>
    </svg>
  );
}

// ── IconKey — utility icon for magicTip callout heading (D6 pfm-43-44 2026-05-09) ──
// Source: Brand/BrandAssets/icons/utility-set-v1/icon-key.svg
// ViewBox 22×22. stroke="currentColor" so it inherits --gold-on-cream from parent.
// Sized by CSS via width/height on .blog-magic-tip__icon rule.
function IconKey({ size }: { size: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22" fill="none"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
      strokeWidth="1.5" aria-hidden="true"
      width={size} height={size} focusable={false}>
      {/* Bow outer ring */}
      <path d="M6.5 3.0 C9.2 2.9 11.0 4.8 10.8 7.0 C10.6 9.4 8.4 11.0 5.8 10.8 C3.2 10.6 1.5 8.5 1.9 6.2 C2.3 3.8 4.2 3.1 6.5 3.0"/>
      {/* Bow inner hole */}
      <path d="M6.4 5.2 C7.6 5.1 8.4 5.9 8.2 7.0 C8.0 8.1 7.0 8.7 5.8 8.6 C4.6 8.5 3.9 7.6 4.2 6.5 C4.5 5.4 5.3 5.3 6.4 5.2" strokeWidth="1.0"/>
      {/* Key shaft */}
      <path d="M9.2 9.8 C12.0 12.4 14.6 14.8 17.8 17.8" strokeWidth="1.6"/>
      {/* Tooth 1 — longer */}
      <path d="M15.2 15.4 C15.8 16.4 16.0 17.4 15.4 18.0" strokeWidth="1.4"/>
      {/* Tooth 2 — shorter */}
      <path d="M16.8 16.8 C17.2 17.6 17.2 18.4 16.8 18.8" strokeWidth="1.3"/>
      {/* Tip rounded end */}
      <path d="M17.8 17.8 C18.4 18.4 18.6 18.8 18.2 19.2" strokeWidth="1.2"/>
    </svg>
  );
}

// ── Shared: the three timeline items content ─────────────────────────────

function VtsTimelineItems({ idPrefix }: { idPrefix: string }) {
  return (
    <div className="vts3-timeline-items">
      {/*
        SVG progression line: organic S-curve in --gold (#D4B86E).
        NOT border-left. NOT a CSS pseudo-element. Inline SVG path with
        cubic bezier curves — Lars's explicit direction: "a line, progression
        line... that doesn't go straight."

        The SVG is absolutely positioned behind the icon column.
        viewBox: 0 0 64 260 — coordinates relative to icon column width (64px).
        Path descends from icon 1 center to icon 3 center, S-meandering
        left-then-right-of-center. Aria §7: control points are a composition
        call adjusted to rendered item heights. Item-gap: --space-64 (64px).
        Icon height: 64px. Total: item1 center at y≈32, item2 at y≈160, item3 at y≈228.

        Color: --gold (#D4B86E) — decorative stroke only, not text. No WCAG req.
        strokeWidth: 1.5px per Aria §7.
        aria-hidden: true — purely decorative.
      */}
      <svg
        className="vts3-connector"
        aria-hidden="true"
        viewBox="0 0 64 260"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M 32 32 C 14 80 50 100 32 160 C 14 218 50 220 32 228"
          stroke="#D4B86E"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {/* ── Item 1: Piano Technique ── */}
      <div className="vts3-item">
        <div className="vts3-item__icon" aria-hidden="true">
          {/*
            icon-philosophy-wrist-freedom — top-down open hand.
            Two-tone: navy hand strokes + gold lateral arc at wrist.
            64px desktop / 48px mobile. No container, no border, no bg.
          */}
          <IconWristFreedom size={64} />
        </div>
        <div className="vts3-item__content">
          <h3 id={`${idPrefix}-item1-title`} className="vts3-item__title">Piano Technique</h3>
          <p className="vts3-item__body">
            {/* Verbatim source text — semicolon after "successfully" per Aria §8 */}
            Learn all the different techniques we use to master Für Elise by Beethoven successfully; arm techniques and finger technique in depth.
          </p>
        </div>
      </div>

      {/* ── Item 2: Music Interpretation ── */}
      <div className="vts3-item">
        <div className="vts3-item__icon" aria-hidden="true">
          {/*
            icon-philosophy-performer-interpretation-v3 — theater mask + gold note.
            Two-tone: navy mask + gold note/staff. 64px desktop / 48px mobile.
          */}
          <IconPerformerInterpretation size={64} />
        </div>
        <div className="vts3-item__content">
          <h3 id={`${idPrefix}-item2-title`} className="vts3-item__title">Music Interpretation</h3>
          <p className="vts3-item__body">
            {/* Verbatim source text — quotation marks around "find the hidden meaning" */}
            The music behind the notes, &ldquo;find the hidden meaning,&rdquo; as Beethoven wrote to Therese. I&apos;ll show you in-depth the meaning of the music and how to achieve this.
          </p>
        </div>
      </div>

      {/* ── Item 3: Watch Für Elise fully played by Lars Nelissen ── */}
      <div className="vts3-item">
        <div className="vts3-item__icon" aria-hidden="true">
          {/*
            icon-philosophy-multi-tempo — three eighth notes, uneven spacing.
            Left+right noteheads navy; center notehead gold. 64px desktop / 48px mobile.
          */}
          <IconMultiTempo size={64} />
        </div>
        <div className="vts3-item__content">
          {/*
            FULL TITLE verbatim from source per Aria §8 — sentence case.
            "Watch Für Elise fully played by Lars Nelissen" — NOT "Watch the Full Performance".
          */}
          <h3 id={`${idPrefix}-item3-title`} className="vts3-item__title">Watch Für Elise fully played by Lars Nelissen</h3>
          <p className="vts3-item__body">
            At the end of the video, I shall play the music from beginning to end without stopping.
          </p>
        </div>
      </div>

    </div>
  );
}

// ── Shared: section header (eyebrow + H2) ─────────────────────────────────

function VtsSectionHeader({ headingId }: { headingId: string }) {
  return (
    <div className="vts3-header">
      {/*
        Eyebrow: --eyebrow-on-cream (#7D6B3B) = 4.9:1 on cream — WCAG AA PASS.
        NOT --gold (#D4B86E) which fails at 2.7:1 on cream (text use).
      */}
      <span className="eyebrow eyebrow--centered eyebrow--on-cream vts3-eyebrow" aria-hidden="true">
        VIDEO TUTORIAL
      </span>
      <h2 id={headingId} className="vts3-h2">How to play Für Elise</h2>
    </div>
  );
}

// ── VideoTutorialSection: Variant B — single full-width column (Lars selected 2026-05-06) ──

function VideoTutorialSection() {
  // No JS-driven reveal. CSS @keyframes vts3FadeIn fires on first paint.
  // Eliminates the React-hydration / IntersectionObserver race (opacity:0 stuck)
  // that broke v3 on staging (2026-05-06, FB ref Eleanor brief Step 3).

  return (
    <section className="video-tutorial-section vts3-section" aria-labelledby="vts3-heading" data-tina-field-overlay>
      <div className="vts3-container">
        <VtsSectionHeader headingId="vts3-heading" />
        {/* Single column — no sidebar, no grid wrapper needed */}
        <div className="vts3-timeline">
          <h3 className="vts3-content-h3">What do you learn in the video?</h3>
          <p className="vts3-intro">
            <em>If you truly wish to master this piece and at the same time improve your piano playing, you should watch this video for sure!</em>
          </p>
          <VtsTimelineItems idPrefix="vts3" />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TranscriptToggle
//
// Renders the Full Tutorial Transcript as a collapsed <details>/<summary>
// element, positioned directly after the YouTube embed in the post body.
// Hidden by default per Lars's editorial preference (2026-05-06).
// Content is still in SSR HTML — Google indexes it; UX is cleaner.
//
// The transcript text is hardcoded here (same pattern as VideoTutorialSection)
// because it is Für Elise-specific content that doesn't benefit from CMS
// editorial control via Tina.
//
// CSS lives in globals.css under .vts-transcript scope.
// ---------------------------------------------------------------------------

function TranscriptToggle() {
  return (
    <details className="vts-transcript" id="transcript-toggle" data-tina-field-overlay>
      <summary>Click to read the Video Script</summary>
      <div className="vts-transcript__body">
        <p>
          This is a video about Für Elise — an in-depth, full-length tutorial about the real technique behind the piece. At the end of the video, you will be able to follow along and practise this piece like a pro.
        </p>
        <p>
          We&apos;re going to talk about the fingerings, the arm movements, the music behind the notes, the different sounds and colors, and how to achieve that. And how to create different exercises from difficult passages. If you stick around until the end, I will play the whole piece from beginning to end without stopping.
        </p>
        <p>
          The character of the main theme is a little bit caressing. &ldquo;Carezzando,&rdquo; we call that in music — it means with a little bit of movement, a little bit forward. Not too slow. The original indication, though, is &ldquo;Molto Grazioso,&rdquo; which means with a lot of grace. And that should also be the character: caressing and graceful.
        </p>
        <p>
          The beginning is very important to achieve pianissimo, but still with a little expression in the sound. You can use the arm in a slightly higher position. Let the weight of the arm rest gently on the keys, and use a rotating movement. If you only play with your fingers, it will sound mechanical, and that&apos;s not desirable. So we always use the arm, trying to find a way to avoid making things sound mechanical.
        </p>
        <p>
          This is a video about &ldquo;Für Elise.&rdquo; It&apos;s an in-depth tutorial, full-length, about the real technique behind the piece. So, at the end of the video, you will be able to follow along and practise this piece like a pro. In the description, I&apos;ve time-stamped everything for you, so you can easily find your way. Also, if you come back a second time. So let&apos;s get to it! My name is Lars from Piano Fantasy. If you&apos;re new to this channel, hit the subscribe button below with the bell icon, so that you don&apos;t miss out on my next videos! If you like this video, don&apos;t forget to give me a thumbs up! Any questions or suggestions? Please leave some comments, and I will answer your questions as best as I can. We&apos;re going to talk about the fingerings, the arm movements, the music behind the notes, the different sounds and colors, and how to achieve that. And how to create different exercises from difficult passages. And if you stick around until the end, I will play the whole piece from beginning to end without stopping.
        </p>
        <p>
          The character of the main theme is a little bit caressing. &ldquo;Carezzando,&rdquo; we call that in music. You see this sometimes written. So it means with a little bit of movement, a little bit forward. Not too slow. The original indication, though, is &ldquo;Molto Grazioso,&rdquo; which means with a lot of grace. And that should also be the character. Caressing and graceful... See! This is the main theme. It sounds free but still in the style, the classical style. Not too much &ldquo;Rubato,&rdquo; a little bit of &ldquo;Rubato&rdquo; in the note, but never &ldquo;Rubato&rdquo; over the whole phrase. Then we have the second theme, which is this: Which is very optimistic, joyful! After that, we get: See, again, there comes back the main theme. It ends up in this third theme or second episode, which is a completely different character: Even frightening almost! Let&apos;s try that: See, I see quite a different character there. And that finishes here quite dramatically... In this kind of brilliant passage, which may be challenging in the beginning, it&apos;s not that difficult. We&apos;ll come to how to practise that.
        </p>
        <p>
          The beginning is very important to achieve pianissimo. But still, with a little expression in the sound, you can use the arm in a slightly higher position. Let the weight of the arm rest gently on the keys, and use a rotating movement. If you only play with your fingers, it will sound mechanical, and that&apos;s not desirable. So, we always use the arm, trying to find a way to avoid making things sound mechanical. I would recommend doing this kind of exercise: exaggerate from one side to another. From here, you should feel the muscles in your fingers. Sometimes, I ask new students, &ldquo;Do you know where the muscles are located in your fingers?&rdquo; Most of the time, they point here. Yes, you do have a few tiny muscles there, but the main muscles are here. So, you should feel it when you press the key. You should feel it in your muscles here. I always feel there&apos;s a direct connection from my lower arm into the keys, even into the mechanics of the piano. I feel the whole piano with my muscles here. Pay a lot of attention to feeling, not just playing. This is very important.
        </p>
        <p>
          Now, we&apos;re going to look at the second theme. For the second theme, you want to be very expressive. The characters that you want to bring out in the second theme — it should sound very lyrical, very legato, very &ldquo;Espressivo,&rdquo; which means very expressive. The second theme should sound very free, very romantic, in a sense. Not too slow, but free. Very lyrical.
        </p>
        <p>
          Now, the accompaniment of the left hand for the second theme. This should sound very clear and very light. The wrist stays in position, and only the fingers move. The fingers move like little hammers here. Very precise, very articulate, but not too heavy. The biggest challenge in Für Elise is this: it&apos;s very easy to play mechanically. So, you have to very consciously work on the expression, on the dynamics, on the phrasing. But I think that if you just think of the basic idea — caressing and graceful — and you try to express that in every single note, you&apos;ll find your way.
        </p>
        <p>
          Now, we&apos;re going to look at some exercises. Start from the beginning of the piece and think of the rotation. The wrist stays low. The wrist moves just a little bit, supporting the arm. Now, I want you to think of the sound. The sound should be very clear, very clean, very silky. Now, let&apos;s work on the middle section — the difficult part. This middle section is in A major, and it&apos;s a very different character from the rest of the piece. It should sound very brilliant, very clear. The arm movements are very important here. Practise slowly with a lot of attention to the arm movements, combining open rotation and vertical arm movements.
        </p>
        <p>
          Now, let&apos;s put it all together. The beginning: pianissimo, caressing, graceful. The second theme: very expressive, very lyrical. The middle section: brilliant, clear. And then, the return to the main theme: peaceful, tender. I made a mistake in playing this piece, an old childhood mistake that didn&apos;t leave me. I tell students to avoid mistakes in the text from the start to avoid wrong muscle memory. Thank you for watching and listening. Don&apos;t hesitate to revisit this video if you need to review certain parts.
        </p>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// stepTimelineItemComponents — minimal component map for the inner TinaMarkdown
// call inside the stepTimeline renderer.
//
// Why separate: `markdownComponents` is a module-level const; referencing it from
// within its own object literal (e.g. in the stepTimeline renderer) would cause
// a ReferenceError at runtime (temporal dead zone). Extracting the stepTimelineItem
// renderer into its own const — defined BEFORE markdownComponents — lets
// stepTimeline's inner <TinaMarkdown components={stepTimelineItemComponents} />
// resolve correctly, so each stepTimelineItem child renders its icon + heading + body
// instead of "<span>No component provided for stepTimelineItem</span>".
//
// Fixed 2026-05-09 per Eleanor verification (cycle 5 continuation).
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stepTimelineItemComponents: Record<string, any> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stepTimelineItem: ({ heading, icon, children }: { heading: string; icon?: any; children?: any }) => (
    <StepTimelineItem heading={heading} icon={icon}>
      {null}
    </StepTimelineItem>
  ),
};

// numberedTimelineItemComponents — parallel to stepTimelineItemComponents.
// Avoids temporal dead zone in markdownComponents self-reference.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const numberedTimelineItemComponents: Record<string, any> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  numberedTimelineItem: ({ heading, icon, children }: { heading: string; icon?: any; children?: any }) => (
    <NumberedTimelineItem heading={heading} icon={icon}>
      {null}
    </NumberedTimelineItem>
  ),
};

// ---------------------------------------------------------------------------
// ImageCaptionFigure — lightbox-clickable wrapper for imageCaption embeds.
//
// D3a (pfm-43-44 continuation 2026-05-09): every imageCaption rendered inside
// a blog post becomes a clickable thumbnail that opens the existing Lightbox
// component (from components/photos/Lightbox.tsx) with the FLIP magic-move
// animation (panel grows from thumbnail rect on open, shrinks back on close).
//
// Architecture: self-managed component with its own useState. Cannot use hooks
// inside the module-level `markdownComponents` const, so this component wraps
// the figure+img and holds lightbox state locally. The Lightbox import is
// already used by app/about/_sections/Path.tsx and Family.tsx.
//
// Intrinsic dimensions (w/h) for the Lightbox aspect-ratio: read from
// img.naturalWidth / img.naturalHeight on click — always accurate, no schema
// change needed on the imageCaption TinaCMS template.
//
// Accessibility:
//   - <button> wraps the <img> only (not the entire figure) so <figcaption>
//     remains independently readable and not nested inside an interactive element.
//   - aria-label = `View image: ${alt}` — descriptive, distinguishes from the
//     surrounding img alt which already describes the image content.
//   - focus returns to the trigger button on Lightbox close (handleClose callback
//     calls buttonRef.current?.focus() before setPhoto(null)).
//   - prefers-reduced-motion: FLIP animation skipped by Lightbox.tsx — no-op.
// ---------------------------------------------------------------------------
function ImageCaptionFigure({
  src,
  alt,
  caption,
  width,
  float: floatDir,
}: {
  src: string;
  alt: string;
  caption?: string;
  width?: string;
  float?: string;
}) {
  const [photo, setPhoto] = useState<LightboxPhoto | null>(null);
  const [sourceRect, setSourceRect] = useState<DOMRect | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => {
    const btn = buttonRef.current;
    const img = imgRef.current;
    if (!btn || !img) return;
    setSourceRect(btn.getBoundingClientRect());
    setPhoto({
      src,
      alt,
      cap: caption,
      // Read intrinsic dimensions from the rendered img element.
      // naturalWidth/naturalHeight are available once the image has loaded.
      // If the image hasn't loaded yet (e.g. below-fold lazy), fall back to
      // the rendered bounding rect dimensions (still valid for aspect-ratio).
      w: img.naturalWidth || img.clientWidth || 800,
      h: img.naturalHeight || img.clientHeight || 600,
    });
  };

  const handleClose = () => {
    setPhoto(null);
    setSourceRect(null);
    // Return focus to the trigger button (WCAG 2.1 SC 2.4.3)
    buttonRef.current?.focus();
  };

  const isFloatRight = floatDir === 'right';
  const isConstrained = width && !isFloatRight;
  const figureClass = [
    'blog-article-body__figure',
    isFloatRight ? 'blog-article-body__figure--float-right' : '',
    isConstrained ? 'blog-article-body__figure--constrained' : '',
  ].filter(Boolean).join(' ');
  const figureStyle = width ? ({ '--figure-constrained-width': width } as React.CSSProperties) : undefined;

  return (
    <>
      <figure className={figureClass} style={figureStyle}>
        {/*
          <button> wraps the <img> only — not figcaption — so the caption
          remains independently accessible outside the interactive element.
          CSS: .blog-figure-lightbox-btn removes all button chrome and lets the
          img fill the button naturally. Cursor: zoom-in signals clickability.
        */}
        <button
          ref={buttonRef}
          className="blog-figure-lightbox-btn"
          aria-label={`View full size: ${alt}`}
          onClick={handleOpen}
          type="button"
        >
          <img
            ref={imgRef}
            src={src}
            alt={alt ?? ''}
            loading="lazy"
            decoding="async"
            className="blog-article-body__img"
          />
        </button>
        {caption && (
          <figcaption className="blog-figure-caption">{caption}</figcaption>
        )}
      </figure>
      {photo && (
        <Lightbox photo={photo} sourceRect={sourceRect} onClose={handleClose} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Custom TinaMarkdown component renderers (add IDs to headings for ToC)
// youtubeEmbed: maps the Tina template to the YouTubeEmbed React component.
// ---------------------------------------------------------------------------
const markdownComponents = {
  h2: ({ children }: { children?: React.ReactNode }) => {
    const id = slugify(extractText(children));
    return <h2 id={id}>{children}</h2>;
  },
  h3: ({ children }: { children?: React.ReactNode }) => {
    const id = slugify(extractText(children));
    return <h3 id={id}>{children}</h3>;
  },
  a: (props: { href?: string; url?: string; children?: React.ReactNode }) => {
    const resolvedHref = props.url ?? props.href ?? '';
    if (isExternalHref(resolvedHref)) {
      return (
        <a href={resolvedHref} target="_blank" rel="noopener noreferrer">
          {props.children}
        </a>
      );
    }
    return <a href={resolvedHref}>{props.children}</a>;
  },
  /*
   * Perf fix (BLOG-WRAPPER-FIX-01 2026-05-06): article body images are below the fold.
   * Without a custom img renderer, TinaMarkdown renders plain <img> without lazy loading,
   * and Next.js static renderer emits <link rel="preload"> hints for every image in the
   * document — including 20+ composer portraits in the Ukrainian post. This floods the
   * preload queue, competing with the hero cover image (the actual LCP element) and
   * raising LCP from ~3s to ~7s on mobile.
   *
   * Fix: intercept img rendering and add loading="lazy" to all article body images.
   * The hero cover image is NOT in the article body — it renders in BlogHero with
   * fetchPriority="high" and is not affected by this component.
   */
  img: (props: { url?: string; alt?: string; caption?: string }) => {
    const src = props.url ?? '';
    return (
      <figure className="blog-article-body__figure">
        <img
          src={src}
          alt={props.alt ?? ''}
          loading="lazy"
          decoding="async"
          className="blog-article-body__img"
        />
        {props.caption && (
          <figcaption className="blog-article-body__caption">{props.caption}</figcaption>
        )}
      </figure>
    );
  },
  /*
   * youtubeEmbed — extended 2026-05-09 to accept optional `id` prop.
   * When `id` is provided it is set on the outer <figure> wrapper via the
   * YouTubeEmbed component's `wrapperId` prop.
   * If not provided, YouTubeEmbed auto-generates `id="video-${videoId}"`.
   * This id is the smooth-scroll target for <watchVideoLink targetId="...">.
   */
  youtubeEmbed: ({ videoId, caption, customThumbnail, id }: { videoId: string; caption?: string; customThumbnail?: string; id?: string }) => (
    <YouTubeEmbed videoId={videoId} caption={caption} customThumbnail={customThumbnail} wrapperId={id ?? `video-${videoId}`} />
  ),
  /*
   * imageCaption — editorial figure with Cormorant italic caption.
   * Replaces bare markdown images that need caption styling (UKRAINIAN-POST-THUMBS-01 2026-05-06).
   * width prop added 2026-05-09 (SUPERFAST-TWEAK-02): optional % string constrains image on ≥768px.
   * CSS: .blog-figure-caption, .blog-article-body__figure--constrained in globals.css.
   */
  /*
   * D1 (pfm-43-44 2026-05-09): float prop added.
   * float="right" + width="60%" → .blog-article-body__figure--float-right with
   * --figure-constrained-width: 60% inline. Desktop: float right with text wrap.
   * Mobile: float cleared, full-width stacked.
   * CSS: .blog-article-body__figure--float-right in globals.css.
   */
  /*
   * imageCaption — D3a (pfm-43-44 continuation 2026-05-09): now delegates to
   * ImageCaptionFigure, which self-manages Lightbox state. Every imageCaption
   * on every blog post becomes a clickable lightbox thumbnail — site-wide.
   * The original figure/img rendering is identical; only the button wrapper
   * and Lightbox mount/unmount are new.
   */
  imageCaption: ({ src, alt, caption, width, float: floatDir }: { src: string; alt: string; caption?: string; width?: string; float?: string }) => (
    <ImageCaptionFigure src={src} alt={alt} caption={caption} width={width} float={floatDir} />
  ),
  /*
   * magicTip — boxed callout aside for "Magic Tip" content.
   * Background: --cream-3. Border: --border-subtle-cream.
   * Semantic: <aside role="note"> for screen reader announcement.
   * Added 2026-05-09 (SUPERFAST-TWEAK-01). CSS: .blog-magic-tip in globals.css.
   *
   * D6 (pfm-43-44 2026-05-09): icon-key.svg from utility set replaces sparkle ✦.
   *   icon-key uses stroke="currentColor" — inherits --gold-on-cream from
   *   .blog-magic-tip__heading color. Size: 32px (within 28-36px brief spec).
   * D7 (pfm-43-44 2026-05-09): heading displays ONLY the `heading` prop value —
   *   no "Magic Tip:" prefix. The icon-key visually flags the callout type.
   *
   * TinaMarkdown template children: the `children` prop is the raw Tina rich-text AST
   * ({ type: 'root', children: [...] }), NOT a rendered ReactNode. Must render via
   * {null} to resolve the AST into React elements.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  magicTip: ({ heading, children }: { heading?: string; children?: any }) => (
    <aside className="blog-magic-tip" role="note" aria-label={heading ?? 'Tip'}>
      <p className="blog-magic-tip__heading">
        {/* icon-key.svg from utility set — replaces sparkle ✦ (D6).
            currentColor inherits --gold-on-cream from .blog-magic-tip__heading.
            aria-hidden: true — decorative; heading text is the semantic label. */}
        <span className="blog-magic-tip__icon" aria-hidden="true">
          <IconKey size={32} />
        </span>
        {/* D7: heading prop value only — no "Magic Tip:" prefix */}
        {heading}
      </p>
      <div className="blog-magic-tip__body">
        {null}
      </div>
    </aside>
  ),
  videoTutorialSection: () => <VideoTutorialSection />,
  /*
   * videoTutorialSectionV2 — child-component parallel to videoTutorialSection.
   * Path B (satie-gymnopedie migration, 2026-05-23).
   *
   * Props: heading (string), intro (string), children (Tina rich-text AST).
   * Children are videoTutorialSectionV2Item nodes — rendered via inner TinaMarkdown
   * with videoTutorialSectionV2ItemComponents map (same pattern as numberedTimeline).
   *
   * videoTutorialSectionV2Item is also registered here so the inner TinaMarkdown
   * call resolves item children correctly (same as stepTimelineItem / numberedTimelineItem).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  videoTutorialSectionV2: ({ heading, intro, children }: { heading?: string; intro?: string; children?: any }) => {
    if (!heading || !intro) return null;
    return (
      <VideoTutorialSectionV2 heading={heading} intro={intro}>
        {null}
      </VideoTutorialSectionV2>
    );
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  videoTutorialSectionV2Item: ({ icon, title, children }: { icon: any; title: string; children?: any }) => (
    <VideoTutorialSectionV2Item icon={icon} title={title}>
      {children}
    </VideoTutorialSectionV2Item>
  ),
  /*
   * transcriptToggle — accepts a `children` rich-text body (per-post transcript).
   *
   * SCHEMA FIX 2026-05-09: field was renamed from `transcript` to `children`.
   * TinaCMS's MDX parser treats named rich-text fields passed as JSX prop values
   * (transcript={<>…</>}) as raw HTML, not as a Tina AST. Using `children` (the
   * TinaCMS body-content convention) and body-syntax MDX is the correct pattern.
   * Content files updated to use <transcriptToggle>…</transcriptToggle> body syntax.
   *
   * Backward-compat: if no `children` prop (= empty block or self-closing
   * <transcriptToggle />) the renderer falls through to the Für Elise
   * hardcoded TranscriptToggle component.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transcriptToggle: ({ children }: { children?: any }) => {
    if (children) {
      return (
        <details className="vts-transcript" id="transcript-toggle" data-tina-field-overlay>
          <summary>Click to read the full transcript</summary>
          <div className="vts-transcript__body">
            {null}
          </div>
        </details>
      );
    }
    // Backward-compat: fall through to Für Elise hardcoded content
    return <TranscriptToggle />;
  },
  /*
   * New components — video-blog migrations cycle 5 (2026-05-09)
   * All per Aria's video-blog component spec.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watchVideoLink: ({ targetId, children }: { targetId: string; children?: any }) => {
    /*
     * Inline-arrow fix (cycle 5 continuation 2026-05-09):
     * TinaMarkdown wraps the rich-text children in a <p> block element,
     * which causes the ::after arrow to render on a new line below the text.
     * Fix: extract plain text from the AST and render it as a text node
     * directly inside the <a> — no block wrapper, arrow stays inline.
     *
     * `children` here is a Tina AST node { type: 'root', children: [...] }.
     * extractTextFromTinaAst() walks the AST and returns the leaf text strings.
     */
    const plainLabel = children?.children
      ? extractTextFromTinaAst(children.children as TinaAstNode[])
      : undefined;
    const displayText = plainLabel || 'Watch the video';
    return (
      <WatchVideoLink targetId={targetId} ariaLabel={plainLabel || undefined}>
        {displayText}
      </WatchVideoLink>
    );
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  philosophyCallout: ({ icon, heading, children }: { icon: any; heading: string; children?: any }) => (
    <PhilosophyCallout icon={icon} heading={heading}>
      {null}
    </PhilosophyCallout>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stepTimeline: ({ children }: { children?: any }) => (
    /*
     * `children` is a Tina rich-text AST (type: 'root') containing stepTimelineItem
     * nodes. Must go through TinaMarkdown so the inner stepTimelineItem renderers
     * get called for each child node.
     *
     * CRITICAL: pass stepTimelineItemComponents so the inner TinaMarkdown call
     * knows how to render <stepTimelineItem> nodes. Without this, TinaMarkdown
     * falls back to the default "No component provided for stepTimelineItem" text.
     * stepTimelineItemComponents is defined above markdownComponents to avoid the
     * temporal dead zone problem of self-referencing const object literals.
     * Fixed 2026-05-09 per Eleanor verification (cycle 5 continuation).
     */
    <StepTimeline>
      {null}
    </StepTimeline>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stepTimelineItem: ({ heading, icon, children }: { heading: string; icon?: any; children?: any }) => (
    <StepTimelineItem heading={heading} icon={icon}>
      {null}
    </StepTimelineItem>
  ),
  philosophyTag: ({ icon, label }: { icon: any; label?: string }) => (  // eslint-disable-line @typescript-eslint/no-explicit-any
    <PhilosophyTag icon={icon} label={label} />
  ),
  /*
   * numberedTimeline + numberedTimelineItem — numbered vts3-style step list.
   * Path B (pfm-43-44 2026-05-09): parallel to stepTimeline. No disc wrapper.
   * Icons: bare philosophy icons on cream rail (no circle container).
   * Numerals: 1.–5. italic gold prefix inside title.
   * Gold S-curve SVG thread: connects icons via .vts3-connector class.
   * CSS: .numbered-timeline__* in globals.css; item + icon inherits .vts3-item__*.
   * Für Elise code path completely untouched (Path B isolation).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  numberedTimeline: ({ heading, children }: { heading?: string; children?: any }) => (
    <NumberedTimeline heading={heading}>
      {null}
    </NumberedTimeline>
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  numberedTimelineItem: ({ heading, icon, children }: { heading: string; icon?: any; children?: any }) => (
    <NumberedTimelineItem heading={heading} icon={icon}>
      {null}
    </NumberedTimelineItem>
  ),
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
// ProDoorProps: plain values from homepage Tina doc (Option A — single source of truth).
// No inline-editing wiring on the blog side; the section is read-only for visitors.
interface ProDoorProps {
  headline: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any;
  ctaLabel: string;
  ctaHref: string;
}

interface Props {
  query: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  slug: string;
  prevNext: PrevNextPosts;
  recentPosts: RecentBlogPost[];
  sidebarQuery: string;
  sidebarVariables: BlogSidebarVariables;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sidebarData: any;
  // New in READ-NEXT + PRO BLOCK + NEWSLETTER brief (2026-05-07)
  relatedPosts: SliderPost[];
  proDoorProps: ProDoorProps;
  /** Cycle 76-A: blog post body as pre-rendered HTML from remark */
  bodyHtml?: string;
}

// ---------------------------------------------------------------------------
// BlogSidebarCards — owns the second useTina call (sidebar global doc).
//
// WHITEOUT FIX (blog-slug-whiteout-2026-05-22):
// Having two useTina() calls in the same component registers two Tina forms
// simultaneously. Tina renders the active form list as an array in the sidebar
// panel without assigning React keys — triggering the
// "Each child in a list should have a unique key prop" hydration warning.
// That warning disrupts getFormAndFieldNameFromMetadata, de-registering the
// Tina form → sidebar unmounts → admin whiteout on click.
//
// Fix: isolate each useTina() call in its own component. BlogPostPageClient
// owns the post useTina; BlogSidebarCards owns the sidebar useTina. React
// never sees both forms as co-located list siblings, eliminating the key-prop
// error at the source.
//
// Drawer vs. sidebar split:
//   - Desktop sidebar (≥1024px): rendered via BlogSidebarCards with full Tina
//     inline-editing field attributes (tinaHeadingField etc.) — editors use
//     the desktop admin, so these matter.
//   - Mobile drawer (<1024px): rendered with plain string values derived from
//     initial sidebarData (no live useTina subscription, no Tina field attrs).
//     Mobile visitors don't use Tina admin — adding a second useTina call for
//     the drawer would re-introduce the double-registration key-prop bug.
// ---------------------------------------------------------------------------
interface BlogSidebarCardsProps {
  sidebarQuery: string;
  sidebarVariables: BlogSidebarVariables;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sidebarData: any;
  // Stateful props from BlogPostPageClient (ToC, reading state, IDs)
  tocItems: TocItem[];
  activeTocId: string;
  recentPosts: RecentBlogPost[];
  tocHeadingId: string;
  relatedHeadingId: string;
  newsletterHeadingId: string;
  proHeadingId: string;
}

function BlogSidebarCards({
  sidebarQuery,
  sidebarVariables,
  sidebarData,
  tocItems,
  activeTocId,
  recentPosts,
  tocHeadingId,
  relatedHeadingId,
  newsletterHeadingId,
  proHeadingId,
}: BlogSidebarCardsProps) {
  const sidebar = sidebarData.blogSidebar;
  const nlHeading = sidebar?.newsletter?.heading ?? 'From the piano';
  const nlMicrocopy = sidebar?.newsletter?.microcopy ?? 'Two short emails a week on technique, musicality, and practice. Leave anytime.';
  const pmHeading = sidebar?.promember?.heading ?? 'Become a Pro Member';
  const pmBody = sidebar?.promember?.body ?? 'Monthly live lessons, daily community, full course library — Join Today. Cancel Anytime.';
  const pmCta = sidebar?.promember?.cta ?? 'See what\'s included';

  return (
    <>
      <TableOfContentsCard
        items={tocItems}
        activeId={activeTocId}
        headingId={tocHeadingId}
      />
      <ContinueReadingCard
        posts={recentPosts}
        headingId={relatedHeadingId}
      />
      <SidebarNewsletterCard
        heading={nlHeading}
        microcopy={nlMicrocopy}
        headingId={newsletterHeadingId}
        idSuffix="sidebar"
      />
      <ProMemberCard
        heading={pmHeading}
        body={pmBody}
        cta={pmCta}
        headingId={proHeadingId}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// CopyPostLinkButton
// ---------------------------------------------------------------------------
function CopyPostLinkButton({ url, title }: { url: string; title: string }) {
  const [isCopied, setIsCopied] = useState(false);

  const handleClick = useCallback(async () => {
    try {
      if (navigator.share) {
        try {
          await navigator.share({ url, title });
          return;
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return;
          // Fall through to clipboard
        }
      }
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard write failed silently
    }
  }, [url, title]);

  return (
    <div className="post-share">
      <button
        type="button"
        className={`post-share__btn${isCopied ? ' is-copied' : ''}`}
        aria-label={isCopied ? 'Post link copied to clipboard' : 'Copy post link to clipboard'}
        onClick={handleClick}
      >
        <svg
          className="post-share__icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.71" />
        </svg>
        <span className="post-share__label">
          {isCopied ? 'Post link copied' : 'Copy post link'}
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TableOfContentsCard
// ---------------------------------------------------------------------------
interface TocCardProps {
  items: TocItem[];
  activeId: string;
  onLinkClick?: () => void;
  headingId: string;
}

function TableOfContentsCard({ items, activeId, onLinkClick, headingId }: TocCardProps) {
  if (!items.length) return null;

  return (
    <div className="sb-card">
      <h2 className="sb-card__title" id={headingId}>In this article</h2>
      <nav aria-label="Table of contents" aria-labelledby={headingId}>
        <ol className="sb-toc__list" style={{ listStyle: 'none' }}>
          {items.map(item => (
            <li
              key={item.id}
              className={[
                'sb-toc__item',
                item.level === 3 ? 'sb-toc__item--h3' : '',
                activeId === item.id ? 'sb-toc__item--active' : '',
              ].filter(Boolean).join(' ')}
              aria-current={activeId === item.id ? 'true' : undefined}
            >
              <a
                className="sb-toc__link"
                href={`#${item.id}`}
                onClick={onLinkClick}
              >
                {item.text}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContinueReadingCard
// ---------------------------------------------------------------------------
interface ContinueReadingCardProps {
  posts: RecentBlogPost[];
  headingId: string;
  /**
   * When true, renders as .sb-card__section (no outer card shell).
   * Use when this component lives inside a shared .sb-card wrapper
   * alongside SidebarNewsletterCard (Fix 1, cycle 74).
   */
  noCard?: boolean;
}

function ContinueReadingCard({ posts, headingId, noCard }: ContinueReadingCardProps) {
  if (!posts.length) return null;

  const catLabel = (cat: string | null) =>
    cat ? (categoryLabels[cat] ?? cat.charAt(0).toUpperCase() + cat.slice(1)) : '';

  return (
    <div className={noCard ? 'sb-card__section' : 'sb-card'}>
      <h2 className="sb-card__title" id={headingId}>Continue reading</h2>
      <ul className="sb-related__list" aria-labelledby={headingId}>
        {posts.map(post => (
          <li key={post.slug}>
            <a className="sb-related__item" href={`/blog/${post.slug}`}>
              {post.coverImage && (
                <img
                  className="sb-related__thumb"
                  src={post.coverImage}
                  alt={post.coverImageAlt ?? post.title}
                  width="60"
                  height="60"
                  loading="lazy"
                />
              )}
              <div className="sb-related__text">
                {post.category && (
                  <span className="sb-related__cat">{catLabel(post.category)}</span>
                )}
                <span className="sb-related__title">{post.title}</span>
                <span className="sb-related__date">{formatDate(post.publishDate)}</span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SidebarNewsletterCard
// Implements the 3-step state machine (email → name → done).
// Cycle 90 (2026-06-08): refactored onto shared useNewsletterSubmit hook — all
// fetch logic, bot guards, Turnstile, and step-transition now live in the hook.
// Visual output, CSS classes, copy, and keyboard behaviour are unchanged.
// ---------------------------------------------------------------------------
interface SidebarNewsletterCardProps {
  heading: string;
  microcopy: string;
  headingId: string;
  /** Unique suffix to avoid duplicate IDs when card renders in sidebar + drawer */
  idSuffix: string;
  /**
   * When true, renders as .sb-card__section (no outer card shell).
   * Use when this component lives inside a shared .sb-card wrapper
   * alongside ContinueReadingCard (Fix 1, cycle 74).
   */
  noCard?: boolean;
}

function SidebarNewsletterCard({
  heading,
  microcopy,
  headingId,
  idSuffix,
  noCard,
}: SidebarNewsletterCardProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const formRenderTimeRef = useRef<number>(Date.now());


  const emailId = `nl-email-${idSuffix}`;
  const nameId = `nl-name-${idSuffix}`;
  const suggestionId = `nl-email-suggestion-${idSuffix}`;
  const hpId = `nl-hp-${idSuffix}`;
  const emailErrorId = `nl-email-err-${idSuffix}`;
  const nameErrorId = `nl-name-err-${idSuffix}`;

  // Cycle 68 Fix 1+2: mailcheck typo-suggest on sidebar email input
  const { checkOnSubmit: mailcheckOnSubmit } = useMailcheck({
    emailInputId: emailId,
    suggestionDivId: suggestionId,
  });

  // Turnstile invisible CAPTCHA — widget mounts off-screen; token is retrieved
  // by the hook at submit time.
  const { containerRef: turnstileRef, getToken: getTurnstileToken, resetWidget: resetTurnstile } = useTurnstile();

  // Cycle 90 (2026-06-08): delegate all submit/fetch logic to the shared hook.
  // Options specific to the sidebar:
  //   stepContainerSelector — sidebar uses .sb-newsletter__step[data-step="%s"],
  //                           not .form-state[data-step="%s"] (the global form pattern).
  //   backButtonSelector    — sidebar back button has class .sb-newsletter__back.
  //   stopPropagation       — iOS drawer: tap events must not bubble to backdrop onClick.
  //   onStepChange          — sync React step state so submit button and done copy
  //                           reflect the current step without extra DOM queries.
  const { isSubmitting, submitError, doneVariant } = useNewsletterSubmit({
    formRef,
    formRenderTimeRef,
    emailInputId: emailId,
    nameInputId: nameId,
    mailcheckOnSubmit,
    getTurnstileToken,
    resetTurnstile,
    stepContainerSelector: '.sb-newsletter__step[data-step="%s"]',
    backButtonSelector: '.sb-newsletter__back',
    stopPropagation: true,
    // 2026-07-24: rail vs. mobile-drawer instance carries a distinct
    // lead_source so a regression in one (e.g. iOS-only drawer bug) is
    // attributable separately from the desktop rail. See lib/form-event-log.ts.
    formSource: idSuffix === 'drawer' ? 'blog-sidebar-drawer' : 'blog-sidebar',
  });

  return (
    <div className={noCard ? 'sb-card__section' : 'sb-card'}>
      <h2
        className="sb-card__title"
        id={headingId}
      >
        {heading}
      </h2>
      <p
        className="sb-newsletter__microcopy"
      >
        {microcopy}
      </p>
      <form
        ref={formRef}
        className="sb-newsletter__form"
        id={`newsletter-form-${idSuffix}`}
        data-state="email"
        action="#"
        method="post"
        noValidate
        aria-labelledby={headingId}
      >
        {/* Honeypot — §15.3 / §05 Layer 2 */}
        <div className="hp-field" aria-hidden="true">
          <label htmlFor={hpId}>Website</label>
          <input type="text" id={hpId} name="website" tabIndex={-1} autoComplete="off" />
        </div>

        {/* Step 1: email */}
        <div className="sb-newsletter__step" data-step="email">
          <label
            htmlFor={emailId}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              fontWeight: 700,
              letterSpacing: 'var(--eyebrow-tracking-compact)',
              textTransform: 'uppercase' as const,
              color: 'var(--eyebrow-on-cream)',
            }}
          >
            Your email
          </label>
          <input
            type="email"
            id={emailId}
            name="email"
            className="sb-newsletter__input"
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
          {/* Cycle 68 Fix 1: mailcheck suggestion slot */}
          <div id={suggestionId} className="mailcheck-suggestion" aria-live="polite" />
          <p className="sb-newsletter__error" id={emailErrorId} role="alert" />
          <button type="submit" className="sb-newsletter__submit">
            Join the Newsletter
          </button>
        </div>

        {/* Step 2: first name — Cycle 70 visual-prominence rebuild */}
        <div className="sb-newsletter__step" data-step="name">
          {/* "Step 2 of 2" pill — unmistakable multi-step indicator */}
          <span className="sb-newsletter__step-indicator" aria-hidden="true">Step 2 of 2</span>
          {/* Large heading — same visual weight as the card title */}
          <p className="sb-newsletter__step2-heading">What&apos;s your first name?</p>
          {/* Explanatory sentence — reassures visitor what it's used for */}
          <p className="sb-newsletter__step2-subtext">We use your first name to greet you personally — nothing else.</p>
          <label htmlFor={nameId} className="sr-only">First name</label>
          <input
            type="text"
            id={nameId}
            name="firstName"
            className="sb-newsletter__input sb-newsletter__input--name-step"
            placeholder="Your first name"
            autoComplete="given-name"
            required
            aria-required="true"
          />
          {submitError && (
            <p className="sb-newsletter__error is-visible" id={nameErrorId} role="alert">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            className="sb-newsletter__submit sb-newsletter__submit--step2"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <span className="lv-inline-loading">
                <OrbitSpinner size={16} aria-label="Signing you up" />
                Signing you up…
              </span>
            ) : 'Complete sign-up →'}
          </button>
          {/* Back button — class .sb-newsletter__back is matched by backButtonSelector
              in the hook's click listener. No React onClick needed. */}
          <button
            type="button"
            className="sb-newsletter__back"
          >
            ← change email
          </button>
        </div>

        {/* Step 3: done */}
        <div className="sb-newsletter__step sb-newsletter__done" data-step="done">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--gold-on-cream)"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="4 12 10 18 20 6" />
          </svg>
          {doneVariant === 'active' ? (
            <>
              <p className="sb-newsletter__done-heading">You&apos;re already subscribed.</p>
              <p className="sb-newsletter__done-body">The Piano Fantasy Minute lands in your inbox twice a week.</p>
            </>
          ) : doneVariant === 'resubscribed' ? (
            <>
              <p className="sb-newsletter__done-heading">Welcome back.</p>
              <p className="sb-newsletter__done-body">You&apos;re back on the list — the Piano Fantasy Minute will start arriving again twice a week.</p>
            </>
          ) : (
            <>
              <p className="sb-newsletter__done-heading">Thanks — you&apos;re in.</p>
              <p className="sb-newsletter__done-body">Check your inbox for a welcome note from Lars.</p>
            </>
          )}
        </div>

        {/* Turnstile invisible widget — off-screen, initialises while user fills the form.
            Same pattern as NewsletterForm.tsx. */}
        <div
          ref={turnstileRef}
          aria-hidden="true"
          style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
        />
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProMemberCard
// ---------------------------------------------------------------------------
interface ProMemberCardProps {
  heading: string;
  body: string;
  cta: string;
  headingId: string;
}

function ProMemberCard({
  heading,
  body,
  cta,
  headingId,
}: ProMemberCardProps) {
  return (
    <div className="sb-card sb-card--pro">
      <h2
        className="sb-promember__heading"
        id={headingId}
      >
        {heading}
      </h2>
      <p
        className="sb-promember__body"
      >
        {body}
      </p>
      <a
        className="sb-promember__cta"
        href="/pro-member"
      >
        {cta}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArticleBodyHtml — memoized dangerouslySetInnerHTML boundary (2026-07-09 fix)
//
// MUST stay memoized with `html` as its only prop. The YouTube-embed hydration
// effect in BlogPostPageClient (below) manually inserts <div class="yt-embed-portal-mount">
// nodes as children of this dangerouslySetInnerHTML container, then portals the
// real <YouTubeEmbed> component into them via createPortal.
//
// Without React.memo here, ANY state update anywhere in BlogPostPageClient
// (e.g. the hydration effect's own setVideoEmbedMounts call, or the ToC
// effect's setTocItems, or the drawer open/close state) re-renders this fiber,
// and React re-applies (resets) the dangerouslySetInnerHTML content — wiping
// every manually-inserted portal mount div along with the whole video embed
// tree, seconds after it was created. Confirmed by direct instrumentation:
// inserted mount divs were `isConnected: true` immediately after insertion,
// then `isConnected: false` within ~500ms of the very setState call that
// registers them. React.memo with a stable `html` prop means this fiber is
// never revisited after initial mount, so the DOM subtree (and anything
// portaled into it) survives for the life of the page.
//
// Do not inline this div directly in BlogPostPageClient's JSX again — that
// regression silently breaks every video embed on every blog post that has
// one, with no visible error (the delegated click listeners for
// imageCaption/transcriptToggle/watchVideoLink are attached to the stable
// <article> ancestor via event bubbling, so they are NOT affected by this —
// only DOM nodes inserted as direct descendants of the html-managed div,
// like the video portal mounts, get silently orphaned).
const ArticleBodyHtml = memo(function ArticleBodyHtml({ html }: { html: string }) {
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function BlogPostPageClient({
  data,
  slug,
  prevNext,
  recentPosts,
  sidebarQuery,
  sidebarVariables,
  sidebarData,
  relatedPosts,
  proDoorProps,
  bodyHtml,
}: Props) {
  const post = data.blog;

  // Drawer sidebar content: derive string values from initial sidebarData prop directly.
  // No useTina subscription here — the drawer is a mobile fallback and mobile users
  // don't use Tina admin. A second useTina would re-introduce the double-registration
  // key-prop bug. Values are SSR-stable (from static props); Tina live updates affect
  // only the desktop sidebar rendered by BlogSidebarCards.
  const staticSidebar = sidebarData.blogSidebar;
  const drawerNlHeading = staticSidebar?.newsletter?.heading ?? 'From the piano';
  const drawerNlMicrocopy = staticSidebar?.newsletter?.microcopy ?? 'Two short emails a week on technique, musicality, and practice. Leave anytime.';
  const drawerPmHeading = staticSidebar?.promember?.heading ?? 'Become a Pro Member';
  const drawerPmBody = staticSidebar?.promember?.body ?? 'Monthly live lessons, daily community, full course library — Join Today. Cancel Anytime.';
  const drawerPmCta = staticSidebar?.promember?.cta ?? 'See what\'s included';

  // subtitle and author fields — added in blog-post-template-g cycle; now in generated types
  const authorName = post.author || 'Lars Nelissen';
  const subtitle = post.subtitle ?? null;

  const categoryValue = post.category ?? null;
  const categoryLabel = categoryValue
    ? (categoryLabels[categoryValue] ?? categoryValue.charAt(0).toUpperCase() + categoryValue.slice(1))
    : null;

  const canonicalUrl = `https://pianofantasy.com/blog/${slug}/`;
  const displayDate = formatDate(post.publishDate);
  // See toIsoDateTimeString's comment above — post.publishDate may already be
  // a native Date object (frontmatter auto-parse), never pass it directly
  // into a JSX attribute.
  const publishDateISO = toIsoDateTimeString(post.publishDate);

  // ToC state
  const articleBodyRef = useRef<HTMLElement>(null);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeTocId, setActiveTocId] = useState<string>('');

  // YouTube embed portal mounts — see the hydration effect below for why this
  // exists (real <YouTubeEmbed> component mount via createPortal, not a DOM hack).
  const [videoEmbedMounts, setVideoEmbedMounts] = useState<Array<{
    container: HTMLDivElement;
    videoId: string;
    caption?: string;
    customThumbnail?: string;
    wrapperId: string;
  }>>([]);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerTabVisible, setIsDrawerTabVisible] = useState(false);
  const drawerPanelRef = useRef<HTMLDivElement>(null);
  const drawerTabRef = useRef<HTMLButtonElement>(null);
  // Phone keyboard pattern (LANE-mobile-keyboard-pattern-everywhere R8, 2026-10-29; hooks/
  // use-phone-keyboard-box.ts). The drawer panel is itself the fixed element, so a plain wrapper
  // is the keyboard host: only while the drawer is open, on a phone with the keyboard up (its
  // newsletter field focused), it fills exactly the visible area and the drawer fills it, keeping
  // its own scroll. Drawer closed / keyboard down / desktop: the wrapper has no box, nothing changes.
  const drawerKbHostRef = useRef<HTMLDivElement>(null);
  const drawerKb = usePhoneKeyboardBox(drawerKbHostRef, isDrawerOpen);
  const heroRef = useRef<HTMLElement>(null);

  // Stable IDs for ARIA
  const uid = useId();
  const tocHeadingId = `toc-heading-${uid}`;
  const tocDrawerHeadingId = `toc-drawer-heading-${uid}`;
  const relatedHeadingId = `related-heading-${uid}`;
  const relatedDrawerHeadingId = `related-drawer-heading-${uid}`;
  const newsletterHeadingId = `newsletter-heading-${uid}`;
  const newsletterDrawerHeadingId = `newsletter-drawer-heading-${uid}`;
  const proHeadingId = `pro-heading-${uid}`;
  const proDrawerHeadingId = `pro-drawer-heading-${uid}`;

  // Build ToC from rendered DOM after mount
  useEffect(() => {
    const el = articleBodyRef.current;
    if (!el) return;
    // Brief delay to ensure TinaMarkdown has rendered
    const t = setTimeout(() => {
      const items = buildTocFromDOM(el);
      setTocItems(items);
      if (items.length > 0) setActiveTocId(items[0].id);
    }, 100);
    return () => clearTimeout(t);
  }, [post.body]);

  // ── YouTube embed hydration — REAL component mount, not a DOM hack (2026-07-09) ──
  //
  // Regression history: blog post bodies are rendered via dangerouslySetInnerHTML
  // (remark → static HTML). remark-shortcodes.ts emits a static facade
  // (figure.yt-embed > div.yt-embed__ratio > button.yt-embed__facade) matching
  // the React <YouTubeEmbed> component's inactive-state DOM.
  //
  // The Cycle 90 "P0 fix" (c1e5a5b, 2026-05-27) patched the frozen-button symptom
  // with a delegated click listener that hand-rolled a raw iframe swap
  // (createElement/replaceChild) entirely OUTSIDE React and OUTSIDE
  // VideoCoordinatorContext. That is the root cause of the 2026-07-09 regression
  // report (sticky/PIP never engages, multiple videos can play at once, no
  // coordinator enforcement) — the hand-rolled iframe had none of the coordinator,
  // sticky/PIP, or single-iframe-PIP logic that lives in YouTubeEmbed.tsx and is
  // proven correct on the About page (VideoBeat.tsx uses the same pattern).
  //
  // Fix: on mount, find every static <figure class="yt-embed"> placeholder inside
  // the article body, read its videoId/caption/customThumbnail back out of the
  // static markup, swap the static figure for an empty mount <div> in the same
  // DOM position, and portal the REAL <YouTubeEmbed> React component into that
  // div via createPortal. createPortal preserves React context across the portal
  // boundary, so each mounted YouTubeEmbed still reads/writes the SAME
  // VideoCoordinatorContext instance provided by <VideoCoordinatorProvider> below
  // — giving blog posts the identical coordinator + sticky/PIP + one-click-play
  // behavior as the About page, instead of a third, incomplete reimplementation.
  //
  // Static markup contract (lib/remark-shortcodes.ts renderYoutubeEmbed):
  //   figure.yt-embed#video-<videoId>
  //     div.yt-embed__ratio
  //       button.yt-embed__facade
  //         img.yt-embed__thumb[src=".../maxresdefault.jpg" or customThumbnail path]
  //       noscript > iframe (crawlable fallback — irrelevant once this JS runs)
  //     figcaption.yt-embed__caption (optional)
  //
  // CRITICAL DEPENDENCY: this effect manually inserts DOM nodes as children of
  // the dangerouslySetInnerHTML container. React re-applies (resets) that
  // container's innerHTML on ANY re-render of its owning fiber — confirmed by
  // direct instrumentation: the inserted mount divs were present and
  // `isConnected: true` immediately after insertion, then `isConnected: false`
  // within ~500ms of the very setState call this effect makes (setState forces
  // a re-render of the whole component tree, including the dangerouslySetInnerHTML
  // host node, which reasserts its __html content and wipes any manually-inserted
  // children). This is why the ArticleBodyHtml component below is wrapped in
  // React.memo with ONLY the html string as its prop — that isolates it so no
  // re-render of BlogPostPageClient (from this effect's setState or any other
  // state change on the page) ever reaches that fiber again after initial mount,
  // so the manually-inserted portal mount divs survive for the life of the page.
  // Do NOT remove the memo wrapper; do NOT inline the dangerouslySetInnerHTML
  // div directly in BlogPostPageClient's JSX again — either regression silently
  // breaks every video embed on every blog post (2026-07-09 fix-of-the-fix).
  useEffect(() => {
    const articleEl = articleBodyRef.current;
    if (!articleEl) return;
    // Guard against double-invocation (React 18 dev StrictMode double-effect):
    // once this article's figures have been swapped for mount points, do not
    // re-scan (the static figures no longer exist on the second pass anyway).
    if (articleEl.dataset.ytEmbedsHydrated === 'true') return;
    articleEl.dataset.ytEmbedsHydrated = 'true';

    const figures = Array.from(articleEl.querySelectorAll<HTMLElement>('figure.yt-embed'));
    if (!figures.length) return;

    const mounts: Array<{ container: HTMLDivElement; videoId: string; caption?: string; customThumbnail?: string; wrapperId: string }> = [];

    figures.forEach(figureEl => {
      const wrapperId = figureEl.getAttribute('id') ?? '';
      const idMatch = wrapperId.match(/^video-([A-Za-z0-9_-]{11})$/);
      if (!idMatch) return;
      const videoId = idMatch[1];

      const thumbImg = figureEl.querySelector<HTMLImageElement>('img.yt-embed__thumb');
      const captionEl = figureEl.querySelector<HTMLElement>('figcaption.yt-embed__caption');
      const caption = captionEl?.textContent?.trim() || undefined;

      // Determine whether the static markup used a custom thumbnail (not the
      // YouTube auto-thumbnail) so the mounted component preserves it exactly.
      let customThumbnail: string | undefined;
      const thumbSrc = thumbImg?.getAttribute('src') ?? '';
      const isAutoThumb = /^https:\/\/img\.youtube\.com\/vi\/[A-Za-z0-9_-]{11}\/maxresdefault\.jpg$/.test(thumbSrc);
      if (thumbSrc && !isAutoThumb) customThumbnail = thumbSrc;

      // Replace the static figure with an empty mount div in the same position.
      // Do NOT portal into the static figure itself — YouTubeEmbed renders its
      // own <figure class="yt-embed">, which would otherwise nest figure-in-figure.
      // RESERVE-MEDIA-SPACE 2026-09-05 (Option D — the video-embed half).
      //
      // The SERVER-RENDERED figure already reserves its box correctly:
      // .yt-embed__ratio is `aspect-ratio: 16/9` and img.yt-embed__thumb carries
      // width="1280" height="720". But removing that figure and inserting an
      // EMPTY <div class="yt-embed-portal-mount"> throws the reservation away —
      // that class has NO CSS RULE ANYWHERE, so the box collapses to zero height
      // until React commits the portal on a later render. Every embed on the page
      // jumps up by its own height and then back down. Measured: div.yt-embed__ratio
      // is a confirmed layout-shift source on the czerny post.
      //
      // Fix: carry the figure's ALREADY-RENDERED height across the swap as a
      // min-height on the mount div. This is a MEASURED value taken from the live
      // box one statement before it is destroyed — not a guessed or uniform ratio.
      // It is cleared in the layout effect below the moment the portal has
      // committed, so the settled page keeps exactly today's geometry and no
      // stale height survives a viewport resize.
      const reservedHeight = figureEl.getBoundingClientRect().height;
      const mountEl = document.createElement('div');
      mountEl.className = 'yt-embed-portal-mount';
      if (reservedHeight > 0) mountEl.style.minHeight = `${reservedHeight}px`;
      figureEl.parentNode?.insertBefore(mountEl, figureEl);
      figureEl.remove();

      mounts.push({ container: mountEl, videoId, caption, customThumbnail, wrapperId });
    });

    if (mounts.length) setVideoEmbedMounts(mounts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // RESERVE-MEDIA-SPACE 2026-09-05: release the transitional min-height set above.
  //
  // By the time this runs, setVideoEmbedMounts has re-rendered and the portalled
  // <YouTubeEmbed> occupies each mount div, so the placeholder height has done its
  // job. Clearing it returns every mount div to `height: auto` — i.e. exactly the
  // geometry the page had before this change, with nothing that could go stale on
  // a viewport resize. useLayoutEffect (not useEffect) so the clear lands in the
  // same frame as the portal commit and the reader never sees an intermediate box.
  useIsomorphicLayoutEffect(() => {
    if (!videoEmbedMounts.length) return;
    videoEmbedMounts.forEach(m => { m.container.style.minHeight = ''; });
  }, [videoEmbedMounts]);

  // ── transcriptToggle accordion hydration (Cycle 91 fix — 2026-05-27) ─────────
  //
  // remark-shortcodes.ts renders <transcriptToggle> as static HTML:
  //   div.blog-transcript-toggle
  //     button.blog-transcript-toggle__btn[aria-expanded="false"]
  //       span (label text)
  //     div.blog-transcript-toggle__body[hidden]
  //       (transcript content)
  //
  // Because the body is dangerouslySetInnerHTML (not a React component tree),
  // React event handlers are never attached to this button. Fix: delegated click
  // listener on the article element. Intercepts clicks on .blog-transcript-toggle__btn,
  // toggles aria-expanded, and toggles the [hidden] attribute on the sibling panel.
  useEffect(() => {
    const articleEl = articleBodyRef.current;
    if (!articleEl) return;

    const handleTranscriptClick = (e: MouseEvent) => {
      // Walk up to find a .blog-transcript-toggle__btn button
      let el = e.target as HTMLElement | null;
      let btn: HTMLButtonElement | null = null;
      while (el && el !== articleEl) {
        if (el.matches('button.blog-transcript-toggle__btn')) {
          btn = el as HTMLButtonElement;
          break;
        }
        el = el.parentElement;
      }
      if (!btn) return;

      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');

      // The body panel is the next sibling element of the button
      const panel = btn.nextElementSibling as HTMLElement | null;
      if (!panel) return;
      if (expanded) {
        panel.setAttribute('hidden', '');
      } else {
        panel.removeAttribute('hidden');
      }
    };

    articleEl.addEventListener('click', handleTranscriptClick);
    return () => articleEl.removeEventListener('click', handleTranscriptClick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── watchVideoLink smooth-scroll hydration (Cycle 91 fix — 2026-05-27) ──────
  //
  // remark-shortcodes.ts renders <watchVideoLink targetId="video-<id>"> as:
  //   <a class="blog-watch-video-link" href="#video-<id>">Watch the video →</a>
  //
  // The href is a fragment pointing at the YouTube embed's <figure id="video-<id>">.
  // Without JS, the browser's default anchor jump causes an instant snap (no smooth
  // scroll). With JS disabled the default is fine; with JS enabled we intercept to
  // scroll smoothly. Also prevents any layout-shift from the instant jump.
  //
  // DEAD-CLICK FIX, ROUND 2 (DEFECT-3, 2026-09-05). Round 1 (8b15252) narrowed the
  // window and did not close it; this replaces it. Measured on deployed staging, the
  // click travels through three distinct DOM states:
  //
  //   State A  SSR/pre-hydration      figure#video-<id> EXISTS. No [data-videoid].
  //   State B  mid-hydration          the effect above has done figureEl.remove() and
  //                                   inserted an EMPTY mount div. NEITHER the figure
  //                                   NOR [data-videoid] exists — the portal has not
  //                                   rendered yet. Round 1 queried [data-videoid] as
  //                                   its fallback, so it missed the only state that
  //                                   actually needed a fallback, and then `return`ed
  //                                   WITHOUT preventDefault().
  //   State C  post-portal            figure#video-<id> is back, with [data-videoid].
  //
  // Two separate holes produced the reported failure:
  //
  //   1. THE CLICK WAS NOT ALWAYS INTERCEPTED. Before this effect runs, no listener
  //      exists at all, so an early click is pure native anchor navigation. That is
  //      why `location.hash` was set on every failure — proof preventDefault() never
  //      ran. Instrumented on a5ecf9e, clicks landed at ~210ms with readyState
  //      "interactive": squarely inside that gap.
  //
  //   2. THE SCROLL WAS FIRE-AND-FORGET. globals.css:465 sets
  //      `html { scroll-behavior: smooth }`, so even the native jump animates, and
  //      the animation is abandoned when its target node is destroyed by the State-B
  //      swap — or is simply finished before late layout shift moves the figure. The
  //      measured trace was figTop -5730 → -1982 → -203 → 0 over ~1.8s; the reported
  //      failures are the runs where that convergence never completed and the reader
  //      was left stranded 4,340px (desktop) / 6,568px (mobile) past the video.
  //
  // The fix does not widen a window — a wider window is what already failed. It makes
  // the intent DURABLE:
  //
  //   • Every .blog-watch-video-link click is intercepted unconditionally. Whether the
  //     target exists at click time is now irrelevant; the native jump never fires.
  //   • The intent is honoured by a correction loop that RE-RESOLVES the target by id
  //     on every frame, so it survives the node being removed and recreated, and keeps
  //     re-pinning through late layout shift for a bounded budget.
  //   • Clicks that happened BEFORE this listener attached are recovered from the
  //     evidence they leave behind — the location hash — on mount and on hashchange.
  //     That covers the whole timeline without an inline pre-hydration script.
  //   • Any genuine user input cancels the loop immediately. We never fight the reader.
  //
  // Listener is on `document` in the CAPTURE phase, not on the article element, so it
  // does not depend on articleBodyRef being populated and runs before anything else.
  useEffect(() => {
    /** Total time we are willing to keep correcting after a click. */
    const CORRECTION_BUDGET_MS = 4000;
    /** Let the smooth animation own the first stretch; only then start snapping. */
    const SMOOTH_PHASE_MS = 900;
    /** Anything inside this is "landed" — passing runs measure figTop 0. */
    const TOLERANCE_PX = 2;

    let cancelActive: (() => void) | null = null;

    /**
     * Scroll to `targetId` and KEEP it there until the layout stops moving.
     * Safe to call when the element does not exist yet — it waits for it.
     */
    function honourIntent(targetId: string) {
      if (cancelActive) cancelActive();

      const start = performance.now();
      // Read the OS reduce-motion preference at CLICK time, not module load — the
      // reader can change it mid-session. `html { scroll-behavior: auto }` under
      // the reduced-motion media query (globals.css) only governs scrolls the
      // BROWSER initiates; an explicit `behavior: 'smooth'` from script overrides
      // the stylesheet entirely, so this path has to honour the preference itself.
      // Without this the CSS fix is bypassed on exactly the case where our handler
      // wins the click — a fully settled page (measured: 22/22 intercepted).
      const prefersReducedMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let raf = 0;
      let smoothIssued = false;
      let stopped = false;
      let lastScrollY = -1;
      let stuckFrames = 0;

      const stop = () => {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(raf);
        window.removeEventListener('wheel', onUserInput);
        window.removeEventListener('touchstart', onUserInput);
        window.removeEventListener('pointerdown', onUserInput);
        window.removeEventListener('keydown', onUserInput);
        if (cancelActive === stop) cancelActive = null;
      };

      // The reader taking over always wins — after a short grace window.
      //
      // The grace matters: the reader almost always WHEEL-SCROLLED to reach this
      // link, so trailing momentum events keep arriving for a moment after the
      // click. Treating those as "they changed their mind" would abandon the
      // scroll mid-flight and strand them — the same lost click by another route,
      // and the likeliest way the reported failure becomes permanent rather than
      // transient. 300ms covers the trailing events without outlasting a real
      // decision to scroll somewhere else.
      //
      // Note we deliberately do NOT listen for 'scroll' — our own corrections fire
      // that, and cancelling on it would make the loop cancel itself immediately.
      const INPUT_GRACE_MS = 300;
      function onUserInput() {
        if (performance.now() - start < INPUT_GRACE_MS) return;
        stop();
      }

      window.addEventListener('wheel', onUserInput, { passive: true });
      window.addEventListener('touchstart', onUserInput, { passive: true });
      window.addEventListener('pointerdown', onUserInput, { passive: true });
      window.addEventListener('keydown', onUserInput);

      const tick = () => {
        if (stopped) return;
        const elapsed = performance.now() - start;

        // Re-resolve every frame. The node captured a frame ago may already have
        // been removed by the portal swap — that staleness is the whole defect.
        const el = document.getElementById(targetId);
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (Math.abs(top) > TOLERANCE_PX) {
            if (!smoothIssued) {
              // The intended UX: one smooth glide to the video — unless the reader
              // has asked for reduced motion, in which case they get the immediate
              // jump the preference means.
              smoothIssued = true;
              el.scrollIntoView({
                behavior: prefersReducedMotion ? 'instant' : 'smooth',
                block: 'start',
              });
            } else if (elapsed > SMOOTH_PHASE_MS) {
              // The glide has had its turn and we are still off target — something
              // moved under it. Snap and hold. 'instant' is required: 'auto' would
              // resolve to the CSS `scroll-behavior: smooth` and restart the very
              // animation we are compensating for.
              el.scrollIntoView({ behavior: 'instant', block: 'start' });

              // If snapping stops changing the scroll position, the target simply
              // cannot reach the top — it is inside the last viewport of the
              // document. Stop rather than re-snapping for the rest of the budget.
              if (Math.round(window.scrollY) === lastScrollY) {
                stuckFrames += 1;
                if (stuckFrames >= 3) {
                  stop();
                  return;
                }
              } else {
                stuckFrames = 0;
              }
              lastScrollY = Math.round(window.scrollY);
            }
          }
        }

        if (elapsed > CORRECTION_BUDGET_MS) {
          stop();
          return;
        }
        raf = requestAnimationFrame(tick);
      };

      cancelActive = stop;
      raf = requestAnimationFrame(tick);
    }

    /** "#anything" → "anything". Used for CLICKS on our own link, where any
     *  fragment the shortcode emitted is legitimately ours to handle. */
    function targetIdFromFragment(hash: string): string | null {
      if (!hash.startsWith('#')) return null;
      let id: string;
      try {
        id = decodeURIComponent(hash.slice(1));
      } catch {
        id = hash.slice(1);
      }
      return id || null;
    }

    /** Strict form, for the hash-RECOVERY path only. On page load the hash could
     *  be any in-page anchor (#faq, a heading link); only a video fragment means
     *  "somebody pressed Watch the video", so only that one gets corrected. */
    function videoTargetFromHash(hash: string): string | null {
      const id = targetIdFromFragment(hash);
      return id && /^video-[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
    }

    const handleWatchVideoClick = (e: MouseEvent) => {
      // Leave modified clicks (new tab / new window / download) to the browser.
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const el = e.target as HTMLElement | null;
      const anchor = el?.closest?.('a.blog-watch-video-link') as HTMLAnchorElement | null;
      if (!anchor) return;

      const targetId = targetIdFromFragment(anchor.getAttribute('href') ?? '');
      if (!targetId) return;

      // UNCONDITIONAL. Round 1's `if (!target) return;` — bailing out before
      // preventDefault when the figure happened to be mid-swap — is the bug.
      e.preventDefault();
      honourIntent(targetId);
    };

    // Recover a click that beat this listener: the native jump it performed set the
    // hash, so the hash is the record of an intent we never got to handle.
    const pending = videoTargetFromHash(window.location.hash);
    if (pending) honourIntent(pending);

    const handleHashChange = () => {
      const id = videoTargetFromHash(window.location.hash);
      if (id) honourIntent(id);
    };

    document.addEventListener('click', handleWatchVideoClick, true);
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      document.removeEventListener('click', handleWatchVideoClick, true);
      window.removeEventListener('hashchange', handleHashChange);
      if (cancelActive) cancelActive();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── imageCaption lightbox hydration (bodyHtml path) ─────────────────────────
  //
  // On the bodyHtml pre-render path (cycle 76-A), remark-shortcodes.ts converts
  // <imageCaption /> to static HTML:
  //
  //   <figure class="image-caption" [style="..."]>
  //     <img class="image-caption__img" src="..." alt="..." loading="lazy" />
  //     [<figcaption class="image-caption__caption">...</figcaption>]
  //   </figure>
  //
  // Because the body is rendered via dangerouslySetInnerHTML (not a React component
  // tree), the ImageCaptionFigure component's React state + Lightbox are never mounted.
  // The image shows but does not enlarge on click.
  //
  // Fix: delegated click listener on articleBodyRef. When a click hits an img inside
  // a figure.image-caption, read its src/alt and the sibling figcaption text, then
  // open the SAME Lightbox component the TinaMarkdown path already uses.
  //
  // The lightbox state (lightboxPhoto, lightboxSourceRect) lives here in the parent
  // so the Lightbox can mount outside the <article> element (same z-index plane as
  // the ImageCaptionFigure path, which mounts Lightbox inside its own return fragment).
  //
  // Regression guard: the ImageCaptionFigure component (TinaMarkdown path) manages
  // its own local state and is completely unaffected by this handler. This handler
  // only fires for clicks inside figure.image-caption elements in the dangerouslySetInnerHTML
  // body; ImageCaptionFigure wraps its img in a <button> so its clicks are consumed
  // by the button's own onClick before bubbling to the delegated listener.
  const [lightboxPhoto, setLightboxPhoto] = useState<LightboxPhoto | null>(null);
  const [lightboxSourceRect, setLightboxSourceRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const articleEl = articleBodyRef.current;
    if (!articleEl) return;

    const handleImageCaptionClick = (e: MouseEvent) => {
      // Walk up from the click target to find an img inside a figure.image-caption
      let el = e.target as HTMLElement | null;
      let img: HTMLImageElement | null = null;
      while (el && el !== articleEl) {
        if (el.matches('figure.image-caption img')) {
          img = el as HTMLImageElement;
          break;
        }
        el = el.parentElement;
      }
      if (!img) return;

      // Confirm parent is figure.image-caption (not ImageCaptionFigure's button path)
      const figure = img.closest('figure.image-caption');
      if (!figure) return;

      // Read src, alt, and optional caption from sibling figcaption
      const src = img.getAttribute('src') ?? '';
      const alt = img.getAttribute('alt') ?? '';
      const captionEl = figure.querySelector<HTMLElement>('.image-caption__caption, figcaption');
      const cap = captionEl?.textContent?.trim() || undefined;

      // Intrinsic dimensions — read from naturalWidth/naturalHeight if loaded, else bounding rect
      const w = img.naturalWidth || img.clientWidth || 800;
      const h = img.naturalHeight || img.clientHeight || 600;

      setLightboxSourceRect(img.getBoundingClientRect());
      setLightboxPhoto({ src, alt, cap, w, h });
    };

    articleEl.addEventListener('click', handleImageCaptionClick);
    return () => articleEl.removeEventListener('click', handleImageCaptionClick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active section tracking
  useEffect(() => {
    if (!tocItems.length) return;
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveTocId(entry.target.getAttribute('id') ?? '');
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 },
    );

    const articleEl = articleBodyRef.current;
    if (!articleEl) return;
    const headings = articleEl.querySelectorAll('h2[id], h3[id]');
    headings.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [tocItems]);

  // Hero IntersectionObserver → drawer tab visibility
  useEffect(() => {
    const heroEl = heroRef.current;
    const tabEl = drawerTabRef.current;
    if (!heroEl || !tabEl) return;

    if (!('IntersectionObserver' in window)) {
      setIsDrawerTabVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsDrawerTabVisible(!entry.isIntersecting);
        });
      },
      { threshold: 0 },
    );
    io.observe(heroEl);
    return () => io.disconnect();
  }, []);

  // Body scroll lock when drawer open
  useEffect(() => {
    document.body.classList.toggle('drawer-open', isDrawerOpen);
    return () => { document.body.classList.remove('drawer-open'); };
  }, [isDrawerOpen]);

  // Esc key closes drawer
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDrawerOpen) closeDrawer();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDrawerOpen]);

  // Resize: auto-close drawer at >= 1024px
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (window.innerWidth >= 1024 && isDrawerOpen) closeDrawer();
      }, 100);
    };
    window.addEventListener('resize', handler);
    return () => { clearTimeout(timer); window.removeEventListener('resize', handler); };
  }, [isDrawerOpen]);

  // Focus trap inside drawer
  useEffect(() => {
    if (!isDrawerOpen) return;
    const panel = drawerPanelRef.current;
    if (!panel) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    panel.addEventListener('keydown', handler);
    return () => panel.removeEventListener('keydown', handler);
  }, [isDrawerOpen]);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
    setTimeout(() => {
      drawerPanelRef.current?.querySelector<HTMLElement>('#drawer-close-btn')?.focus();
    }, 50);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setTimeout(() => drawerTabRef.current?.focus(), 50);
  }, []);

  const closeDrawerOnNavClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <VideoCoordinatorProvider>
    <div className="blog-post-layout">
      <ReadingProgressBar />

      {/* ── HERO ── */}
      <header className="blog-hero" ref={heroRef} aria-labelledby="blog-post-h1">
        <div className={`blog-hero__inner${!post.coverImage ? ' blog-hero__inner--no-cover' : ''}`}>
          {post.coverImage && (
            <div
              className={`blog-hero__image-col${
                post.coverWidth && post.coverHeight && post.coverHeight > post.coverWidth
                  ? ' blog-hero__image-col--portrait'
                  : ''
              }`}
            >
              {/* The whole cover shows at its own shape: width/height are the served
                  file's real pixel size (page.tsx, build-time manifest), CSS never crops. */}
              <img
                className="blog-hero__cover"
                src={post.coverImage}
                alt={post.coverImageAlt ?? ''}
                {...(post.coverWidth && post.coverHeight
                  ? { width: post.coverWidth, height: post.coverHeight }
                  : {})}
                fetchPriority="high"
              />
            </div>
          )}
          <div className="blog-hero__text-col">
            {categoryLabel && (
              <p className="blog-hero__eyebrow" aria-label={`Category: ${categoryLabel}`}>
                {categoryLabel}
              </p>
            )}
            <h1
              id="blog-post-h1"
              className="blog-hero__title"
            >
              {noOrphan(post.title)}
            </h1>
            <p className="blog-hero__byline">
              <span>
                Written by {authorName}
              </span>
              <span className="sep" aria-hidden="true">·</span>
              <time
                dateTime={publishDateISO}
              >
                {displayDate}
              </time>
            </p>
          </div>
        </div>
        {subtitle && (
          <div className="blog-hero__subtitle-band">
            <div className="blog-hero__subtitle-band-inner">
              <p
                className="blog-hero__subtitle"
              >
                {subtitle}
              </p>
            </div>
          </div>
        )}
      </header>

      {/* ── BREADCRUMB STRIP ── */}
      <nav className="blog-breadcrumb-strip" aria-label="Breadcrumb">
        <ol className="blog-breadcrumb-strip__list">
          <li><a href="/">Home</a></li>
          <li><a href="/blog">Blog</a></li>
          {categoryLabel && (
            <li>
              {/* Category archive pages are not yet built; link to /blog for now.
                  JSON-LD breadcrumb includes the canonical /blog/[category]/ URL. */}
              <a href="/blog">{categoryLabel}</a>
            </li>
          )}
          <li><span aria-current="page">{post.title}</span></li>
        </ol>
      </nav>

      {/* ── PREV / NEXT STRIP — under hero, before article body ── */}
      {/*
        Position: directly after breadcrumb strip, BEFORE .blog-article-layout.
        This is the original pre-fff77e4 position.
        BLOG-WRAPPER-FIX-01 (fff77e4) incorrectly moved this to the bottom of the article.
        Lars's correction (BLOG-WRAPPER-FIX-02 2026-05-06): must be under the hero,
        not at the bottom. Restored to original position here.
      */}
      <nav className="blog-prevnext" aria-label="Article navigation">
        {prevNext.prev ? (
          <Link
            className="blog-prevnext__col blog-prevnext__col--prev"
            href={`/blog/${prevNext.prev.slug}`}
          >
            <p className="blog-prevnext__title">{prevNext.prev.title}</p>
            <div className="blog-prevnext__label">
              <span className="blog-prevnext__arrow" aria-hidden="true">←</span>
              <span>Previous Post</span>
            </div>
          </Link>
        ) : (
          <div className="blog-prevnext__col blog-prevnext__col--prev blog-prevnext__col--empty" aria-hidden="true" />
        )}
        <div className="blog-prevnext__divider" aria-hidden="true" />
        {prevNext.next ? (
          <Link
            className="blog-prevnext__col blog-prevnext__col--next"
            href={`/blog/${prevNext.next.slug}`}
          >
            <p className="blog-prevnext__title">{prevNext.next.title}</p>
            <div className="blog-prevnext__label">
              <span>Next Post</span>
              <span className="blog-prevnext__arrow" aria-hidden="true">→</span>
            </div>
          </Link>
        ) : (
          <div className="blog-prevnext__col blog-prevnext__col--next blog-prevnext__col--empty" aria-hidden="true" />
        )}
      </nav>

      {/* ── ARTICLE BODY + SIDEBAR ── */}
      <div className="blog-article-layout">
        <div className="blog-article-layout__inner">

          <article
            className="blog-article-body"
            ref={articleBodyRef}
            aria-label={post.title}
          >
            <ArticleBodyHtml html={bodyHtml ?? ''} />
            {/* PFM-CANONICAL-01 2026-05-07: dynamic PFM footer — renders only when pfmNumber is set in frontmatter.
                The <hr> uses the .blog-article-layout hr rule (globals.css) which gives a muted divider at 15% text-on-cream opacity.
                The tagline <em> is italic by HTML semantics; font-style and color inherit from .blog-article-body p.
                Punctuation byte-exact: em-dash U+2014 before edition label, en-dash U+2013 with spaces around ` – `,
                curly apostrophe U+2019 in "You're", piano emoji U+1F3B9. */}
            {post.pfmNumber != null && (
              <>
                <hr />
                <p className="pfm-footer-tagline">
                  <em>
                    {`Piano Fantasy Minute${post.pfmEditionLabel ? ` — ${post.pfmEditionLabel} ` : ' '}#${post.pfmNumber} – You’re reading the 🎹 Piano Fantasy Minute — part of a growing series of short, practical piano insights, published twice per week.`}
                  </em>
                </p>
              </>
            )}
            <CopyPostLinkButton url={canonicalUrl} title={post.title} />
          </article>

          {/* Desktop sidebar — hidden at < 1024px, drawer takes over */}
          {/*
            Stack order (BLOG-WRAPPER-FIX-01 2026-05-06):
            TOC → Continue Reading → Newsletter Signup → Pro Member (bottom/last).
            Pro Member is LAST — it's the conversion CTA and Lars wants it at the end.
            Each card is a separate .sb-card shell — no noCard merging.
            TOC card (first-child) uses flex-grow:1 to fill available sidebar height;
            its list scrolls internally so entries past Reinhold Glière stay accessible.
            BlogSidebarCards owns the sidebar useTina — see component comment for
            the key-prop whiteout fix rationale.
          */}
          <aside className="blog-sidebar" aria-label="Article sidebar">
            <BlogSidebarCards
              sidebarQuery={sidebarQuery}
              sidebarVariables={sidebarVariables}
              sidebarData={sidebarData}
              tocItems={tocItems}
              activeTocId={activeTocId}
              recentPosts={recentPosts}
              tocHeadingId={tocHeadingId}
              relatedHeadingId={relatedHeadingId}
              newsletterHeadingId={newsletterHeadingId}
              proHeadingId={proHeadingId}
            />
          </aside>

        </div>
      </div>

      {/* ── THREE BOTTOM SECTIONS — inserted 2026-05-07 (READ-NEXT + PRO BLOCK + NEWSLETTER brief) ── */}
      {/* All three sit INSIDE .blog-post-layout, BELOW .blog-article-layout, ABOVE the drawer tab. */}

      {/* §A READ-NEXT SLIDER */}
      <ReadNextSlider posts={relatedPosts} />

      {/* §B PRO MEMBERSHIP BLOCK — data from homepage Tina doc (Option A: single source of truth) */}
      <ProMemberBlock
        headline={proDoorProps.headline}
        body={proDoorProps.body}
        ctaLabel={proDoorProps.ctaLabel}
        ctaHref={proDoorProps.ctaHref}
      />

      {/* §C NEWSLETTER SIGNUP */}
      <NewsletterForm />

      {/* ── DRAWER TAB — fixed right rail, visible at < 1024px after hero scrolls out ── */}
      <button
        ref={drawerTabRef}
        className={`blog-drawer-tab${isDrawerTabVisible ? ' is-visible' : ''}`}
        id="blog-drawer-tab"
        type="button"
        aria-controls="blog-drawer-panel"
        aria-expanded={isDrawerOpen}
        onClick={openDrawer}
      >
        <svg
          className="blog-drawer-tab__chevron"
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 6l-6 6 6 6" />
        </svg>
        <span className="blog-drawer-tab__label">Contents &amp; more</span>
      </button>

      {/* ── BACKDROP ── */}
      <div
        className={`blog-drawer-backdrop${isDrawerOpen ? ' is-open' : ''}`}
        id="blog-drawer-backdrop"
        aria-hidden="true"
        onClick={closeDrawer}
      />

      {/* ── DRAWER PANEL ── (inside its phone keyboard host, see drawerKb above) */}
      <div
        ref={drawerKbHostRef}
        className={`kb-vv-wrap blog-drawer-kb${drawerKb.on ? ' kb-vv--on' : ''}`}
        style={drawerKb.style}
      >
      <div
        ref={drawerPanelRef}
        className={`blog-drawer${isDrawerOpen ? ' is-open' : ''}`}
        data-kb-vv-card=""
        id="blog-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Article sidebar"
        aria-hidden={!isDrawerOpen}
      >
        <button
          id="drawer-close-btn"
          className="blog-drawer__close"
          type="button"
          aria-label="Close sidebar"
          onClick={closeDrawer}
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6l-12 12" />
          </svg>
        </button>

        <div className="blog-drawer__cards">
          {/*
            Drawer card order mirrors desktop sidebar (BLOG-WRAPPER-FIX-01 2026-05-06):
            TOC → Continue Reading → Newsletter Signup → Pro Member (bottom/last).
            Pro Member is LAST — conversion CTA, Lars wants it at the end of the stack.
            noCard merging removed — each is a distinct .sb-card box.

            Drawer uses static sidebarData values (no useTina subscription — see
            BlogSidebarCards comment and drawerNlHeading/drawerPmHeading derivation above).
            No Tina field attributes on drawer cards (mobile users do not use Tina admin).
          */}
          <TableOfContentsCard
            items={tocItems}
            activeId={activeTocId}
            onLinkClick={closeDrawerOnNavClick}
            headingId={tocDrawerHeadingId}
          />
          <ContinueReadingCard
            posts={recentPosts}
            headingId={relatedDrawerHeadingId}
          />
          <SidebarNewsletterCard
            heading={drawerNlHeading}
            microcopy={drawerNlMicrocopy}
            headingId={newsletterDrawerHeadingId}
            idSuffix="drawer"
          />
          <ProMemberCard
            heading={drawerPmHeading}
            body={drawerPmBody}
            cta={drawerPmCta}
            headingId={proDrawerHeadingId}
          />
        </div>
      </div>
      </div>

    </div>

      {/*
        FloatingMiniPlayer removed — PIP-RESTART-FIX-01 (2026-05-06).
        Floating PIP is now handled inside YouTubeEmbed.tsx via single-iframe
        CSS-class toggle (no second iframe, no playback restart on scroll-back).
        VideoCoordinatorProvider stays — drives single-play coordination.
      */}

      {/* ── YouTube embed real-component portals (2026-07-09 regression fix) ──
        Each entry in videoEmbedMounts is a static facade figure that was
        swapped for an empty <div> at hydration time (see the effect above).
        Portaling the real <YouTubeEmbed> component into that div — while still
        rendering inside this <VideoCoordinatorProvider> subtree — gives every
        blog embed the SAME coordinator + sticky/PIP + one-click-play behavior
        as the About page's VideoBeat, because createPortal preserves React
        context across the portal boundary (only the DOM parent changes).
      ── */}
      {videoEmbedMounts.map(m => createPortal(
        <YouTubeEmbed
          key={m.wrapperId}
          videoId={m.videoId}
          caption={m.caption}
          customThumbnail={m.customThumbnail}
          wrapperId={m.wrapperId}
        />,
        m.container,
      ))}

      {/* ── bodyHtml imageCaption lightbox (cycle 92 fix) ─────────────────────
        Renders the shared Lightbox for clicks on figure.image-caption img
        elements in the dangerouslySetInnerHTML body. The TinaMarkdown path
        (ImageCaptionFigure) manages its own lightbox state independently;
        this mount point is only for the bodyHtml pre-render path.
        Identical props pattern to ImageCaptionFigure's Lightbox usage above.
      ── */}
      {lightboxPhoto && (
        <Lightbox
          photo={lightboxPhoto}
          sourceRect={lightboxSourceRect}
          onClose={() => {
            setLightboxPhoto(null);
            setLightboxSourceRect(null);
          }}
        />
      )}
    </VideoCoordinatorProvider>
  );
}
